import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext.js';
import { useWebSocket } from '../context/WebSocketContext.js';
import { api } from '../api/client.js';
import { copyToClipboard } from '../utils/clipboard.js';
import { countPagesInRange, clampPageRange } from '@printbridge/shared';
import {
  Printer,
  FileText,
  Download,
  CheckCircle2,
  AlertCircle,
  Clock,
  Trash2,
  Layers,
  Palette,
  Send,
  Plus,
  RefreshCw,
  ShieldAlert,
  Copy,
  Check,
  ShieldCheck,
  Smartphone,
  QrCode,
  X,
  Sparkles,
  RotateCcw,
  AlertTriangle,
  Key,
  Lock,
  Unlock,
  Settings,
  BarChart3,
  MessageSquare,
  LayoutGrid,
  Wifi,
  WifiOff,
  Star,
  ToggleLeft,
  ToggleRight,
  ChevronRight,
  Monitor,
  Sliders,
  IndianRupee,
  Minus,
  Receipt,
  Upload,
  Laptop,
  FolderOpen,
} from 'lucide-react';

// =============================================================================
// TYPE HELPERS
// =============================================================================
type ColorMode = 'BW' | 'COLOR';
type Orientation = 'portrait' | 'landscape' | 'auto';

// =============================================================================
// MAIN DASHBOARD COMPONENT
// =============================================================================
export const DashboardPage: React.FC<{
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
  adminShopId?: string | null;
  showSimulateModal?: boolean;
  setShowSimulateModal?: (show: boolean) => void;
}> = ({
  activeTab = 'queue',
  setActiveTab,
  adminShopId,
  showSimulateModal: propShowSimulate,
  setShowSimulateModal: propSetShowSimulate,
}) => {
    const { user, shop, refreshProfile } = useAuth();
    const { orders, setOrders, refreshQueue, currentAdminShopId, setAgentOnline } = useWebSocket();
    const effectiveShopId = adminShopId || currentAdminShopId || shop?.id;

    // Tab state
    const [internalTab, setInternalTab] = useState('queue');
    const currentTab = setActiveTab ? (activeTab || 'queue') : internalTab;

    const changeTab = (tab: string) => {
      setInternalTab(tab);
      if (setActiveTab) setActiveTab(tab);
    };

    // Toast
    const [toast, setToast] = useState<{ show: boolean; title: string; message: string; color: 'green' | 'amber' | 'red' }>({ show: false, title: '', message: '', color: 'green' });

    const showToast = (title: string, message: string, color: 'green' | 'amber' | 'red' = 'green') => {
      setToast({ show: true, title, message, color });
      setTimeout(() => setToast((p) => ({ ...p, show: false })), 4000);
    };

    // WhatsApp Web Multi-Device (Option 2 - 100% Free Forever)
    const [baileysState, setBaileysState] = useState<{
      status: 'DISCONNECTED' | 'SCAN_QR' | 'CONNECTING' | 'CONNECTED';
      qrDataUrl?: string | null;
      phone?: string | null;
      connectedAt?: string | null;
      lastError?: string | null;
    } | null>(null);
    const [loadingBaileys, setLoadingBaileys] = useState(false);

    const loadBaileysStatus = async () => {
      if (!effectiveShopId) return;
      try {
        const data = await api.getShopBaileysStatus(effectiveShopId);
        setBaileysState(data);
      } catch { }
    };

    const handleStartBaileys = async (forceParam?: boolean | React.MouseEvent) => {
      const force = forceParam === true;
      if (!effectiveShopId) return;
      setLoadingBaileys(true);
      try {
        const data = await api.startShopBaileys(effectiveShopId, force);
        setBaileysState(data);
        await loadDiagnostics();
      } catch (err: any) {
        showToast('WhatsApp Error', err.message || 'Failed to start pairing', 'red');
      } finally {
        setLoadingBaileys(false);
      }
    };

    const handleDisconnectBaileys = async () => {
      if (!confirm('Are you sure you want to unlink WhatsApp from this shop?')) return;
      setLoadingBaileys(true);
      try {
        await api.disconnectShopBaileys(effectiveShopId);
        await loadBaileysStatus();
        await loadDiagnostics();
        showToast('WhatsApp Unlinked', 'Device disconnected from PrintBridge', 'amber');
      } catch (err: any) {
        showToast('Error', err.message || 'Failed to disconnect', 'red');
      } finally {
        setLoadingBaileys(false);
      }
    };

    useEffect(() => {
      const handleQr = (e: any) => {
        const detail = e.detail;
        setBaileysState((prev) => ({
          ...prev,
          status: 'SCAN_QR',
          qrDataUrl: detail.qrDataUrl,
        }));
      };
      const handleStatus = (e: any) => {
        const detail = e.detail;
        setBaileysState((prev) => ({
          ...prev,
          status: detail.status,
          phone: detail.phone,
        }));
        loadDiagnostics();
      };
      const handleSwitchesUpdated = (e: any) => {
        if (e.detail?.features) {
          setShopSwitches((prev) => ({ ...prev, ...e.detail.features }));
        }
      };

      window.addEventListener('baileys_qr', handleQr);
      window.addEventListener('baileys_status', handleStatus);
      window.addEventListener('switches_updated', handleSwitchesUpdated);
      return () => {
        window.removeEventListener('baileys_qr', handleQr);
        window.removeEventListener('baileys_status', handleStatus);
        window.removeEventListener('switches_updated', handleSwitchesUpdated);
      };
    }, []);

    // Archive queue state (orders skipped 2 times)
    const [archivedOrders, setArchivedOrders] = useState<any[]>([]);
    const [queueFilter, setQueueFilter] = useState<'live' | 'archived'>('live');
    const [loadingArchived, setLoadingArchived] = useState(false);

    const loadArchivedQueue = async () => {
      if (!effectiveShopId) return;
      setLoadingArchived(true);
      try {
        const data = await api.getArchivedQueue(effectiveShopId);
        setArchivedOrders(data || []);
      } catch (e: any) {
        console.warn('Could not fetch archived orders:', e.message);
      } finally {
        setLoadingArchived(false);
      }
    };

    // 1-Click Agent Download State
    const [downloadingAgent, setDownloadingAgent] = useState(false);
    const handleDownloadAgent = async () => {
      if (!effectiveShopId) {
        showToast('Error', 'No shop selected', 'red');
        return;
      }
      setDownloadingAgent(true);
      try {
        await api.downloadAgentZip(effectiveShopId, shop?.name);
        showToast('Agent Downloaded', 'Agent package downloaded successfully! Extract and double-click START_AGENT.bat', 'green');
      } catch (e: any) {
        console.error('Agent download error:', e);
        showToast('Download Failed', e.message || 'Could not download agent package', 'red');
      } finally {
        setDownloadingAgent(false);
      }
    };

    // Admin unlock
    const [isAdminUnlocked, setIsAdminUnlocked] = useState(false);
    const [adminKey, setAdminKey] = useState('');
    const [adminConfig, setAdminConfig] = useState({
      phoneId: '1409709458888361',
      wabaId: '1621075809487683',
      verifyToken: 'print_shop_secret_2026',
      graphEndpoint: 'https://graph.facebook.com/v21.0/',
      accessToken: '',
      manualDir: '%USERPROFILE%/Desktop/Manual_Print',
    });

    // Printer Management State (synced with Windows & persisted)
    const [printers, setPrinters] = useState<Array<{ id: string; name: string; systemName?: string; enabled: boolean; type: string; duplexSupported?: boolean }>>(() => {
      try {
        const saved = localStorage.getItem('printbridge_printers');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            return parsed.map((p) => ({
              ...p,
              systemName: p.systemName || p.name,
              duplexSupported: p.duplexSupported !== undefined ? p.duplexSupported : (p.systemName?.toLowerCase().includes('pantum -printer') ? true : false),
            }));
          }
        }
      } catch { }
      return [
        { id: 'pdf', name: 'Microsoft Print to PDF', systemName: 'Microsoft Print to PDF', enabled: true, type: 'Virtual PDF', duplexSupported: false },
        { id: 'pantum-usb', name: 'Pantum USB (Counter)', systemName: 'Pantum -Printer', enabled: true, type: 'USB Laser', duplexSupported: true },
        { id: 'pantum-main', name: 'Pantum Wi-Fi (Main)', systemName: 'Pantum-M6500NW-Series 5804F8', enabled: true, type: 'Network Laser', duplexSupported: false },
        { id: 'onenote', name: 'OneNote (Virtual)', systemName: 'OneNote (Desktop)', enabled: false, type: 'Virtual', duplexSupported: false },
      ];
    });

    const togglePrinter = (id: string) => {
      setPrinters((prev) => {
        const next = prev.map((p) => (p.id === id ? { ...p, enabled: !p.enabled } : p));
        try { localStorage.setItem('printbridge_printers', JSON.stringify(next)); } catch { }
        return next;
      });
      const pr = printers.find((p) => p.id === id);
      showToast('Printer Updated', `${pr?.name} is now ${!pr?.enabled ? 'turned ON (Active)' : 'turned OFF'}`, !pr?.enabled ? 'green' : 'amber');
    };

    // Add & Delete Printer States
    const [showAddPrinterModal, setShowAddPrinterModal] = useState(false);
    const [newPrinterDisplayName, setNewPrinterDisplayName] = useState('');
    const [newPrinterSystemName, setNewPrinterSystemName] = useState('');
    const [newPrinterType, setNewPrinterType] = useState('USB Laser');
    const [newPrinterDuplex, setNewPrinterDuplex] = useState(true);

    const handleAddPrinter = (e: React.FormEvent) => {
      e.preventDefault();
      const displayName = newPrinterDisplayName.trim();
      const systemName = newPrinterSystemName.trim() || displayName;
      if (!displayName) {
        showToast('Name Required', 'Please enter a display name for this printer.', 'red');
        return;
      }
      const newPrinter = {
        id: 'printer-' + Date.now(),
        name: displayName,
        systemName: systemName,
        enabled: true,
        type: newPrinterType || 'Laser Printer',
        duplexSupported: newPrinterDuplex,
      };
      setPrinters((prev) => {
        const next = [...prev, newPrinter];
        try { localStorage.setItem('printbridge_printers', JSON.stringify(next)); } catch { }
        return next;
      });
      setNewPrinterDisplayName('');
      setNewPrinterSystemName('');
      setNewPrinterDuplex(true);
      setShowAddPrinterModal(false);
      showToast('Printer Added! 🖨️', `"${displayName}" (${systemName}) is now added (${newPrinterDuplex ? 'Auto-Duplex' : 'Simplex'}).`, 'green');
    };

    const handleTogglePrinterDuplex = (printerId: string) => {
      setPrinters((prev) => {
        const next = prev.map((p) => p.id === printerId ? { ...p, duplexSupported: p.duplexSupported === false ? true : false } : p);
        try { localStorage.setItem('printbridge_printers', JSON.stringify(next)); } catch { }
        return next;
      });
      const target = printers.find((p) => p.id === printerId);
      const newDuplexState = target?.duplexSupported === false;
      showToast('Duplex Setting Updated', `${target?.name || 'Printer'} set to ${newDuplexState ? 'Auto-Duplex (2-Sided)' : '1-Sided (Simplex Only)'}`, 'green');
    };

    const handleDeletePrinter = (id: string, name: string) => {
      if (!window.confirm(`Are you sure you want to remove "${name}" from available printers?`)) {
        return;
      }
      setPrinters((prev) => {
        const next = prev.filter((p) => p.id !== id);
        try { localStorage.setItem('printbridge_printers', JSON.stringify(next)); } catch { }
        return next;
      });
      showToast('Printer Removed', `"${name}" removed from printer list.`, 'amber');
    };

    const handleResetDefaultPrinters = () => {
      const defaults = [
        { id: 'pdf', name: 'Microsoft Print to PDF', systemName: 'Microsoft Print to PDF', enabled: true, type: 'Virtual PDF', duplexSupported: false },
        { id: 'pantum-usb', name: 'Pantum USB (Counter)', systemName: 'Pantum -Printer', enabled: true, type: 'USB Laser', duplexSupported: true },
        { id: 'pantum-main', name: 'Pantum Wi-Fi (Main)', systemName: 'Pantum-M6500NW-Series 5804F8', enabled: true, type: 'Network Laser', duplexSupported: false },
        { id: 'onenote', name: 'OneNote (Virtual)', systemName: 'OneNote (Desktop)', enabled: false, type: 'Virtual', duplexSupported: false },
      ];
      setPrinters(defaults);
      try { localStorage.setItem('printbridge_printers', JSON.stringify(defaults)); } catch { }
      showToast('Printers Reset', 'Default test printers restored.', 'green');
    };

    // Modal states
    const [internalSimulateModal, setInternalSimulateModal] = useState(false);
    const showSimulateModal = propShowSimulate !== undefined ? propShowSimulate : internalSimulateModal;
    const setShowSimulateModal = propSetShowSimulate || setInternalSimulateModal;

    const [simName, setSimName] = useState('Kavita Krishnan');
    const [simPhone, setSimPhone] = useState('919822334455');
    const [simFileType, setSimFileType] = useState<'pdf' | 'docx' | 'image'>('pdf');
    const [simFileName, setSimFileName] = useState('Chemistry_Lab_Report.pdf');
    const [simPageCount, setSimPageCount] = useState<number>(12);
    const [isSimulating, setIsSimulating] = useState(false);

    const [manualAlertModal, setManualAlertModal] = useState<{ show: boolean; fileName: string; path: string }>({ show: false, fileName: '', path: '' });

    const [shopSwitches, setShopSwitches] = useState({
      adminMessagingAllowed: true,
      allMessagesEnabled: true,
      welcomeMessageEnabled: true,
      orderReceivedEnabled: true,
      printingConfirmationEnabled: true,
      orderRevivedNoticeEnabled: true,
      turnNextEnabled: true,
      expiryNoticeEnabled: true,
    });
    const [switchesSaving, setSwitchesSaving] = useState(false);

    const [advanceModal, setAdvanceModal] = useState<{
      show: boolean;
      targetType: 'file' | 'order';
      targetId: string;
      targetName: string;
      options: { duplex: boolean; copies: number; pageRange: string; pageCount: number; colorMode: ColorMode; orientation: Orientation; fitToPage: boolean };
    }>({
      show: false, targetType: 'file', targetId: '', targetName: '',
      options: { duplex: true, copies: 1, pageRange: '', pageCount: 1, colorMode: 'BW', orientation: 'auto', fitToPage: true },
    });

    const [messages, setMessages] = useState({
      welcomeMessage: 'Welcome to {{shopName}}! 🖨️ Please send your PDF for direct printing, or any other document/image (Word, Excel, PowerPoint, Photos) to be downloaded for manual printing.',
      orderReceived: 'Your {{fileCount}} file(s) collected! {{customersAheadText}} We will notify you when printing begins.',
      printingConfirmation: 'Your {{fileCount}} file(s) have finished printing! Please collect them from the counter.',
      expiryNotice: 'Notice: Your files have been automatically cleared from the queue due to inactivity to protect privacy. Please resend if you still need them printed.',
    });
    const [messagesSaving, setMessagesSaving] = useState(false);
    const [messagesSuccess, setMessagesSuccess] = useState(false);
    const [activeMessageEdit, setActiveMessageEdit] = useState<'welcomeMessage' | 'orderReceived' | 'printingConfirmation' | 'expiryNotice'>('welcomeMessage');

    const [analytics, setAnalytics] = useState<any>(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [chartFilter, setChartFilter] = useState<'today' | 'yesterday' | 'week' | 'month' | 'year'>('today');
    const [chartMetric, setChartMetric] = useState<'both' | 'sheets' | 'revenue'>('both');

    const [diagnostics, setDiagnostics] = useState<any>(null);
    const [showWaModal, setShowWaModal] = useState(false);
    const [testingWa, setTestingWa] = useState(false);
    const [waTestResult, setWaTestResult] = useState<any>(null);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    // Pricing & Color Configuration State
    const [pricingConfig, setPricingConfig] = useState({
      bwSingle: 2.0,
      bwDouble: 1.5, // per page (i.e. ₹3 per sheet)
      colorSingle: 10.0,
      colorDouble: 8.0, // per page (i.e. ₹16 per sheet)
      colorPrintEnabled: true,
    });
    const [pricingSaving, setPricingSaving] = useState(false);

    // Printer Selection Modal State
    const [printerSelectModal, setPrinterSelectModal] = useState<{
      show: boolean;
      orderId: string;
      fileId?: string;
      selectedPrinterId: string;
      isManual: boolean;
      isReprint: boolean;
      orderNumber?: number | string;
      totalBill: number;
      fileCount: number;
    }>({
      show: false,
      orderId: '',
      selectedPrinterId: '',
      isManual: false,
      isReprint: false,
      totalBill: 0,
      fileCount: 0,
    });

    // Settings sub-tab
    const [settingsSection, setSettingsSection] = useState<'printers' | 'pricing' | 'subscribers' | 'agent' | 'whatsapp' | 'system' | 'devices'>('printers');

    // Browser Devices state for counter access management
    const [shopDevices, setShopDevices] = useState<any[]>([]);
    const [loadingShopDevices, setLoadingShopDevices] = useState(false);

    const loadShopDevices = async () => {
      if (!effectiveShopId) return;
      setLoadingShopDevices(true);
      try {
        const list = await api.getShopkeeperDevices(effectiveShopId);
        setShopDevices(list || []);
      } catch (err: any) {
        console.warn('Failed to load shop devices:', err?.message);
      } finally {
        setLoadingShopDevices(false);
      }
    };

    const handleToggleShopDeviceApproval = async (deviceId: string, approved: boolean) => {
      try {
        await api.toggleShopkeeperDeviceApproval(deviceId, approved, effectiveShopId);
        showToast(
          approved ? 'Access Granted! 🔐' : 'Access Revoked! 🚫',
          approved ? 'Browser device approved. Counter terminal access granted.' : 'Browser authorization revoked. Active sessions terminated.',
          approved ? 'green' : 'amber'
        );
        await loadShopDevices();
      } catch (err: any) {
        showToast('Error', err.message || 'Failed to update device authorization', 'red');
      }
    };

    const handleDeleteShopDevice = async (deviceId: string) => {
      if (!confirm('Are you sure you want to permanently revoke and delete this browser device? The device will be logged out immediately.')) return;
      try {
        // Step 1: Explicitly revoke access first
        await api.toggleShopkeeperDeviceApproval(deviceId, false, effectiveShopId).catch(() => {});
        // Step 2: Permanently delete
        await api.deleteShopkeeperDevice(deviceId, effectiveShopId);
        showToast('Device Deleted', 'Browser access revoked and record removed completely.', 'green');
        await loadShopDevices();
      } catch (err: any) {
        showToast('Error', err.message || 'Failed to delete device', 'red');
      }
    };

    // Customer Subscriptions state
    const [customerSubs, setCustomerSubs] = useState<any[]>([]);
    const [loadingSubs, setLoadingSubs] = useState(false);
    const [subForm, setSubForm] = useState({ phone: '', label: '', days: 30 });
    const [subSaving, setSubSaving] = useState(false);

    const loadCustomerSubs = async () => {
      if (!effectiveShopId) return;
      setLoadingSubs(true);
      try {
        const data = await api.getShopSubscriptions(effectiveShopId);
        setCustomerSubs(data || []);
      } catch (err: any) {
        console.warn('Failed to load subscriptions:', err?.message);
      } finally {
        setLoadingSubs(false);
      }
    };

    const handleAddSubscriber = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!subForm.phone.trim()) {
        showToast('Error', 'Please enter customer phone number', 'red');
        return;
      }
      setSubSaving(true);
      try {
        await api.createShopSubscription({
          phone: subForm.phone.trim(),
          label: subForm.label.trim() || undefined,
          days: subForm.days,
        }, effectiveShopId);
        showToast('Subscriber Enrolled! ⭐', `${subForm.phone} priority subscription pass active for ${subForm.days} days!`, 'green');
        setSubForm({ phone: '', label: '', days: 30 });
        await loadCustomerSubs();
        await refreshQueue();
      } catch (err: any) {
        showToast('Error', err?.message || 'Failed to save subscription', 'red');
      } finally {
        setSubSaving(false);
      }
    };

    const handleExtendSubscriber = async (subId: string, addDays: number) => {
      try {
        await api.updateShopSubscription(subId, { addDays }, effectiveShopId);
        showToast('Pass Extended! ⭐', `Added +${addDays} days to priority subscription!`, 'green');
        await loadCustomerSubs();
        await refreshQueue();
      } catch (err: any) {
        showToast('Error', err?.message || 'Failed to extend subscription', 'red');
      }
    };

    const handleDeleteSubscriber = async (subId: string, phone: string) => {
      if (!confirm(`Revoke priority subscription pass for ${phone}?`)) return;
      try {
        await api.deleteShopSubscription(subId, effectiveShopId);
        showToast('Subscriber Revoked', `Priority pass removed for ${phone}.`, 'green');
        await loadCustomerSubs();
        await refreshQueue();
      } catch (err: any) {
        showToast('Error', err?.message || 'Failed to delete subscription', 'red');
      }
    };

    const handleCopy = async (text: string, key: string) => {
      await copyToClipboard(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2500);
    };

    const loadDiagnostics = async () => {
      if (!effectiveShopId) return;
      try {
        const data = await api.getDiagnostics(effectiveShopId);
        setDiagnostics(data);
        if (data?.agent?.online !== undefined) {
          setAgentOnline(Boolean(data.agent.online));
        }
      } catch { /* silently fail */ }
    };

    useEffect(() => {
      loadDiagnostics();
      loadBaileysStatus();
      refreshQueue();
      loadArchivedQueue();
      const iv = setInterval(() => {
        loadDiagnostics();
        loadBaileysStatus();
        refreshQueue();
        loadArchivedQueue();
      }, 5000);
      return () => clearInterval(iv);
    }, [effectiveShopId]);

    useEffect(() => {
      const handleJobFailed = (e: any) => {
        const job = e.detail;
        let msg = String(job?.error || 'Execution failed');
        if (msg.includes('META_TOKEN_EXPIRED') || msg.includes('Error 190')) {
          msg = 'Meta WhatsApp Access Token expired (Error 190). Please regenerate it in Meta Developer Portal.';
        }
        showToast('Print Job Issue', msg, 'red');
      };
      window.addEventListener('print_job_failed', handleJobFailed);
      return () => window.removeEventListener('print_job_failed', handleJobFailed);
    }, []);

    useEffect(() => {
      // Load queue, archived queue, shop settings, message templates, switches & pricing
      if (effectiveShopId) {
        refreshQueue();
        loadArchivedQueue();
        loadShopDevices();
        api.getShopSettings(effectiveShopId).then((res) => {
          if (res?.messages) setMessages((p) => ({ ...p, ...res.messages }));
          if (res?.features) setShopSwitches((p) => ({ ...p, ...res.features }));
          if (res?.system) {
            setPricingConfig((p) => ({
              bwSingle: res.system.bwPricePerPage !== undefined ? Number(res.system.bwPricePerPage) : p.bwSingle,
              bwDouble: res.system.bwDoublePricePerPage !== undefined ? Number(res.system.bwDoublePricePerPage) : p.bwDouble,
              colorSingle: res.system.colorPricePerPage !== undefined ? Number(res.system.colorPricePerPage) : p.colorSingle,
              colorDouble: res.system.colorDoublePricePerPage !== undefined ? Number(res.system.colorDoublePricePerPage) : p.colorDouble,
              colorPrintEnabled: res.system.colorPrintEnabled !== undefined ? Boolean(res.system.colorPrintEnabled) : p.colorPrintEnabled,
            }));
          }
        }).catch(() => { });
      }
    }, [effectiveShopId]);

    useEffect(() => {
      if (currentTab === 'analytics') {
        setAnalyticsLoading(true);
        api.getAnalytics(effectiveShopId).then((res) => { setAnalytics(res); setAnalyticsLoading(false); });
      }
    }, [currentTab, effectiveShopId]);

    useEffect(() => {
      const handleDevicesChanged = () => {
        loadShopDevices();
      };
      window.addEventListener('devices_changed', handleDevicesChanged);
      return () => window.removeEventListener('devices_changed', handleDevicesChanged);
    }, [effectiveShopId]);

    useEffect(() => {
      if (currentTab === 'settings' && settingsSection === 'devices') {
        loadShopDevices();
        const interval = setInterval(loadShopDevices, 8000);
        return () => clearInterval(interval);
      }
    }, [currentTab, settingsSection, effectiveShopId]);

    // =====  PRICING & UTILITY HELPERS  =====
    // Helper to calculate cost for a file:
    // In 2-sided (duplex) printing, back-to-back page pairs use double rate,
    // and any odd leftover page (printed on 1 side of the last sheet) uses the single-sided rate.
    const calculateFilePrice = (
      pages: number,
      copies: number,
      duplex: boolean,
      isColor: boolean,
      pricing: {
        bwSingle: number;
        bwDouble: number;
        colorSingle: number;
        colorDouble: number;
      }
    ): number => {
      const validPages = Math.max(1, pages);
      const validCopies = Math.max(1, copies);
      const singleRate = isColor ? pricing.colorSingle : pricing.bwSingle;
      const doubleRate = isColor ? pricing.colorDouble : pricing.bwDouble;

      if (!duplex) {
        return Math.max(1, Math.round(validPages * singleRate * validCopies));
      }

      const doublePages = Math.floor(validPages / 2) * 2;
      const oddPages = validPages % 2;
      const costPerCopy = doublePages * doubleRate + oddPages * singleRate;
      return Math.max(1, Math.round(costPerCopy * validCopies));
    };

    const calculateOrderBill = (order: any): number => {
      const files = (order.files || []).filter((f: any) => f.status !== 'REVOKED');
      const orderCopies = Math.max(1, order.copies || 1);
      const orderDuplex = order.duplex !== false;
      const isOrderColor = (order.colorMode || order.color_mode) === 'COLOR' && pricingConfig.colorPrintEnabled;

      let total = 0;
      let pdfCount = 0;

      for (const f of files) {
        // STRICT USER RULE: Only count PDF documents towards automated money/bill.
        // Manual print documents (Word, Excel, Images, PPT, etc.) are printed manually and excluded from the automated total!
        const ext = (f.extension || '').toLowerCase().replace(/^\./, '');
        const fName = (f.fileName || f.file_name || '').toLowerCase();
        const routeType = f.routeType || f.route_type;
        const isPdf = routeType === 'DIRECT' || ext === 'pdf' || fName.endsWith('.pdf');

        if (!isPdf) {
          continue; // Skip manual print documents
        }

        pdfCount++;
        const rawDocPages = Math.max(1, f.pageCount ?? f.page_count ?? 1);
        const fileOpts = f.printOptions || f.print_options || {};
        const fileDuplex = fileOpts.duplex !== undefined ? Boolean(fileOpts.duplex) : orderDuplex;
        const fileColor = (fileOpts.colorMode ?? (isOrderColor ? 'COLOR' : 'BW')) === 'COLOR' && pricingConfig.colorPrintEnabled;

        const fileCopies = fileOpts.copies !== undefined && Number(fileOpts.copies) >= 1
          ? Number(fileOpts.copies)
          : orderCopies;

        const clampedFileRange = clampPageRange(fileOpts.pageRange, rawDocPages);
        const effectivePages = countPagesInRange(clampedFileRange, rawDocPages);

        const fileCost = calculateFilePrice(
          effectivePages,
          fileCopies,
          fileDuplex,
          fileColor,
          pricingConfig
        );
        total += fileCost;
      }

      if (total === 0 && pdfCount > 0) {
        total = pdfCount * (orderDuplex ? pricingConfig.bwDouble : pricingConfig.bwSingle);
      }

      return Math.round(total);
    };

    const formatCustomerPhone = (raw: string) => {
      const digits = (raw || '').replace(/\D/g, '');
      if (digits.length < 4) {
        return { prefix: '+' + digits, last3: digits };
      }

      // If unresolved Baileys privacy LID (14+ digits not starting with 91)
      if (digits.length >= 14 && !digits.startsWith('91')) {
        return { prefix: 'WhatsApp User', last3: '#' + digits.slice(-4) };
      }

      // If standard Indian number with 91 prefix (12 digits, e.g. 919428553290)
      if (digits.length === 12 && digits.startsWith('91')) {
        const without91 = digits.slice(2);
        return {
          prefix: `+91 ${without91.slice(0, 5)} ${without91.slice(5, 7)}`,
          last3: without91.slice(7),
        };
      }

      // If standard Indian number without 91 prefix (10 digits, e.g. 9428553290)
      if (digits.length === 10) {
        return {
          prefix: `+91 ${digits.slice(0, 5)} ${digits.slice(5, 7)}`,
          last3: digits.slice(7),
        };
      }

      const prefixDigits = digits.slice(0, -3);
      const last3 = digits.slice(-3);
      let formattedPrefix = '+' + prefixDigits;
      if (prefixDigits.startsWith('91') && prefixDigits.length > 2) {
        formattedPrefix = '+91 ' + prefixDigits.slice(2);
      }
      return { prefix: formattedPrefix, last3 };
    };

    const handleToggleMasterDuplex = async (order: any) => {
      const nextDuplex = !(order.duplex !== false);
      // Optimistically update order and all its files immediately so prices update without waiting
      setOrders((prev) =>
        prev.map((o) => {
          if (o.id !== order.id) return o;
          const updatedFiles = (o.files || []).map((f: any) => ({
            ...f,
            printOptions: {
              ...(f.printOptions || f.print_options || {}),
              duplex: nextDuplex,
            },
            print_options: {
              ...(f.print_options || f.printOptions || {}),
              duplex: nextDuplex,
            },
          }));
          return {
            ...o,
            duplex: nextDuplex,
            files: updatedFiles,
          };
        })
      );

      try {
        await api.updateOrderOptions(order.id, { duplex: nextDuplex }, effectiveShopId);
        showToast('Duplex Changed', `Order #${order.queueNumber || order.id.slice(0, 8)} & all files set to ${nextDuplex ? '2-Sided' : '1-Sided'}`, 'green');
        await refreshQueue();
      } catch (err: any) {
        showToast('Error', err.message, 'red');
        await refreshQueue();
      }
    };

    const handleToggleMasterColor = async (order: any) => {
      if (!pricingConfig.colorPrintEnabled) {
        showToast('Color Disabled', 'Color printing is turned OFF in Settings.', 'amber');
        return;
      }
      const isCurrentColor = (order.colorMode || order.color_mode) === 'COLOR';
      const nextColor = isCurrentColor ? 'BW' : 'COLOR';
      try {
        await api.updateOrderOptions(order.id, { colorMode: nextColor }, effectiveShopId);
        showToast('Color Changed', `Order & all files set to ${nextColor === 'COLOR' ? 'Colour' : 'B&W'}`, 'green');
        await refreshQueue();
      } catch (err: any) {
        showToast('Error', err.message, 'red');
      }
    };

    const openPrinterSelectModal = (order: any, isManual: boolean = false, isReprint: boolean = false, fileId?: string, rank?: number) => {
      if (!order.files || order.files.length === 0) {
        showToast('No Documents', 'This customer order does not have any attached files.', 'amber');
        return;
      }
      const enabledPrinters = printers.filter((p) => p.enabled);
      if (enabledPrinters.length === 0) {
        showToast('No Active Printer', 'All printers are turned OFF in Settings! Please turn ON at least one printer.', 'red');
        return;
      }
      const bill = calculateOrderBill(order);

      setPrinterSelectModal({
        show: true,
        orderId: order.id,
        fileId,
        selectedPrinterId: enabledPrinters[0]?.id || '',
        isManual,
        isReprint,
        orderNumber: rank ?? order.rank ?? order.queueNumber ?? (order as any).queue_number,
        totalBill: bill,
        fileCount: (order.files || []).length,
      });
    };

    const handleExecutePrint = async (specificPrinterId?: string) => {
      const printerIdToUse = specificPrinterId || printerSelectModal.selectedPrinterId;
      const { orderId, isManual, isReprint, totalBill, fileId } = printerSelectModal;
      const selectedPrinter = printers.find((p) => p.id === printerIdToUse);
      setPrinterSelectModal((p) => ({ ...p, show: false }));

      const targetOrder = orders.find((o: any) => o.id === orderId) || archivedOrders.find((o: any) => o.id === orderId);
      const targetFiles = targetOrder?.files || [];
      const nonRevokedFiles = targetFiles.filter((f: any) => f.status !== 'REVOKED');
      const manualFiles = nonRevokedFiles.filter((f: any) => {
        const ext = (f.extension || f.fileName || '').toLowerCase();
        const routeType = f.routeType || f.route_type;
        return routeType === 'MANUAL' || !ext.endsWith('.pdf');
      });

      if (isManual) {
        await api.triggerPrint(orderId, { action: 'SEND_TO_DESKTOP', fileId: fileId || undefined }, effectiveShopId);
        showToast('Manual Print Saved', 'Non-PDF file saved directly to Desktop for manual printing.', 'amber');
      } else {
        const targetSystemPrinter = selectedPrinter?.systemName || selectedPrinter?.name;
        await api.triggerPrint(orderId, {
          action: 'PRINT',
          printerId: printerIdToUse,
          printerName: targetSystemPrinter,
          fileId: fileId || undefined,
        }, effectiveShopId);

        // If order contained any non-PDF files alongside PDF, alert shopkeeper that they are on Desktop
        if (manualFiles.length > 0 && !fileId) {
          showToast(
            'Print Spooled & Files Exported 🖨️',
            `PDF files sent to ${selectedPrinter?.name || 'printer'}. ${manualFiles.length} non-PDF file(s) saved directly to Desktop for manual printing.`,
            'amber'
          );
        } else {
          showToast(
            isReprint ? 'Re-Print Dispatched! 🖨️' : 'Print Dispatched! 🖨️',
            `Spooled to ${selectedPrinter?.name || 'Selected Printer'}. After print finishes, click "Confirm Done" to collect ₹${totalBill}!`,
            'green'
          );
        }
      }
      await refreshQueue();
    };

    const handleUpdateFilePageCount = async (file: any, newCount: number) => {
      const validCount = Math.max(1, Math.min(9999, newCount));
      try {
        await api.updateFileOptions(file.id, { pageCount: validCount }, effectiveShopId);
        showToast('Page Count Updated', `${file.fileName || file.file_name || 'Document'} updated to ${validCount} pages`, 'green');
        await refreshQueue();
      } catch (err: any) {
        showToast('Error', err.message, 'red');
      }
    };

    const handleConfirmOrderDone = async (order: any, bill: number) => {
      const files = order.files || [];
      const nonRevokedFiles = files.filter((f: any) => f.status !== 'REVOKED');
      const isPrintDone = (order.printStatus || order.print_status) === 'PRINT_DONE' || (nonRevokedFiles.length > 0 && nonRevokedFiles.every((f: any) => f.status === 'PRINTED'));
      const isPrinting = order.status === 'PRINTING' || (order.printStatus || order.print_status) === 'PRINTING';
      const hasPrintedOnce = isPrintDone || isPrinting || nonRevokedFiles.some((f: any) => f.status === 'PRINTED' || f.status === 'PRINTING');

      if (!hasPrintedOnce) {
        showToast('Print Required First', 'Please click "Print" to print documents before confirming handover!', 'amber');
        return;
      }

      try {
        await api.completeOrderManual(order.id, effectiveShopId);
        showToast(
          'Order Completed & Billed! ✅',
          `Collected ₹${bill}. Customer notified on WhatsApp and ₹${bill} added to Daily Profit!`,
          'green'
        );
        await refreshQueue();
        if (currentTab === 'analytics') {
          const res = await api.getAnalytics(effectiveShopId);
          setAnalytics(res);
        }
      } catch (err: any) {
        showToast('Completion Error', err.message, 'red');
      }
    };

    const handleDirectFileUpload = async (fileId: string, event: React.ChangeEvent<HTMLInputElement>) => {
      const uploadedFile = event.target.files?.[0];
      if (!uploadedFile) return;

      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Data = reader.result as string;
          showToast('Processing File...', `Reading ${uploadedFile.name} & auto-detecting pages...`, 'amber');
          const res = await api.uploadFile(fileId, { base64Data, fileName: uploadedFile.name }, effectiveShopId);
          showToast('File Ready! 📄', `${uploadedFile.name} loaded (${res.pageCount || res.file?.pageCount || 1} pages). Price updated!`, 'green');
          await refreshQueue();
        } catch (err: any) {
          showToast('Upload Error', err.message || 'Failed to upload file', 'red');
        }
      };
      reader.readAsDataURL(uploadedFile);
    };

    const handleSavePricing = async () => {
      setPricingSaving(true);
      try {
        await api.updateSystemConfig({
          bwPricePerPage: pricingConfig.bwSingle,
          bwDoublePricePerPage: pricingConfig.bwDouble,
          colorPricePerPage: pricingConfig.colorSingle,
          colorDoublePricePerPage: pricingConfig.colorDouble,
          colorPrintEnabled: pricingConfig.colorPrintEnabled,
          currency: 'INR',
          timezone: 'Asia/Kolkata',
          utcOffsetMinutes: 330,
        }, effectiveShopId);
        showToast('Rates Saved! 💰', 'Auto-calculation pricing and Color toggle updated.', 'green');
      } catch (err: any) {
        showToast('Error', err.message, 'red');
      } finally {
        setPricingSaving(false);
      }
    };

    // =====  HANDLERS  =====
    const handleSimulateSubmit = async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      setIsSimulating(true);
      try {
        await api.simulateTestOrder({
          name: simName,
          phone: simPhone,
          fileType: simFileType,
          fileName: simFileName,
          pageCount: simPageCount,
        }, effectiveShopId);
        setShowSimulateModal(false);
        showToast('Order Injected', `Order for ${simName} (${simPageCount} pages) added to queue!`, 'green');
        await refreshQueue(); await loadDiagnostics();
      } catch (err: any) { showToast('Simulation Error', err.message || 'Failed to simulate', 'red'); }
      finally { setIsSimulating(false); }
    };

    const handleClearDemoOrders = async () => {
      if (confirm('Clear all demo orders from the queue?')) {
        try {
          await api.clearDemoOrders(effectiveShopId);
          showToast('Queue Cleared', 'All demo orders removed. Ready for live WhatsApp messages!', 'green');
          await refreshQueue();
        } catch (err: any) { showToast('Error', err.message, 'red'); }
      }
    };

    const handleTestExpiration = async () => {
      try {
        await api.testExpiration(effectiveShopId);
        await refreshQueue();
        showToast('Expiry Check', '23-hour inactivity worker executed. Buffer purged.', 'green');
      } catch (err: any) { showToast('Error', err.message, 'red'); }
    };

    const handleSimulateRevocation = async (fileId: string) => {
      try {
        await api.simulateRevocation(fileId, effectiveShopId);
        await refreshQueue();
        showToast('Revocation', 'File purged from queue and local buffer.', 'amber');
      } catch (err: any) { showToast('Error', err.message, 'red'); }
    };

    const handleOrderAction = async (orderId: string, hasManualFiles: boolean) => {
      if (hasManualFiles) {
        await api.triggerPrint(orderId, { action: 'SEND_TO_DESKTOP' }, effectiveShopId);
        showToast('Manual Print', 'File saved to Desktop for manual printing.', 'amber');
      } else {
        await api.triggerPrint(orderId, { action: 'PRINT' }, effectiveShopId);
        showToast('Print Dispatched', `Document spooled to ${printers.find(p => p.enabled)?.name || 'Printer'}! WhatsApp sent.`, 'green');
      }
      await refreshQueue();
    };

    const handleCancelOrder = async (orderId: string) => {
      if (confirm('Purge this order and wipe all buffer files from disk?')) {
        try {
          await api.cancelOrder(orderId, effectiveShopId);
          showToast('Order Purged', 'Files wiped and order removed.', 'green');
          await refreshQueue();
          await loadArchivedQueue();
        } catch (err: any) { showToast('Error', err.message, 'red'); }
      }
    };

    const handleTurnNextOrder = async (orderId: string) => {
      try {
        const res = await api.turnNextOrder(orderId, effectiveShopId);
        if (res?.order?.status === 'ARCHIVED') {
          showToast('Auto-Archived (Skipped 2×)', 'Customer skipped 2 times. Order moved to Archive to clear the counter!', 'amber');
        } else {
          showToast('Turn Next', 'Customer moved to queue end. Next customer called!', 'amber');
        }
        await refreshQueue();
        await loadArchivedQueue();
      } catch (err: any) { showToast('Error', err.message, 'red'); }
    };

    const handleUnarchiveOrder = async (orderId: string) => {
      try {
        await api.unarchiveOrder(orderId, effectiveShopId);
        showToast('Order Restored! 🚀', 'Order moved from archive back to active live queue.', 'green');
        await refreshQueue();
        await loadArchivedQueue();
      } catch (err: any) { showToast('Error', err.message, 'red'); }
    };

    const handleToggleSwitch = async (key: keyof typeof shopSwitches) => {
      const isLockedByAdmin = shopSwitches.adminMessagingAllowed === false;
      const isShopkeeper = user?.role === 'shopkeeper';

      if (isLockedByAdmin && isShopkeeper) {
        showToast('Access Denied', 'Outbound WhatsApp messaging has been disabled by Platform Administrator for this shop. Counter staff cannot turn it on.', 'red');
        return;
      }

      const updated = { ...shopSwitches, [key]: !shopSwitches[key] };
      setShopSwitches(updated);
      setSwitchesSaving(true);
      try {
        await api.updateShopSwitches(updated, effectiveShopId);
        showToast('Switch Updated', `Notification ${updated[key] ? 'enabled' : 'disabled'}`, 'green');
      } catch (err: any) { setShopSwitches(shopSwitches); showToast('Error', err.message, 'red'); }
      finally { setSwitchesSaving(false); }
    };

    const handleToggleFileDuplex = async (file: any, currentDuplex: boolean) => {
      const nextDuplex = !currentDuplex;
      // Optimistically update this file in local state immediately so file & order price updates instantly
      setOrders((prev) =>
        prev.map((o) => {
          const hasFile = (o.files || []).some((f: any) => f.id === file.id);
          if (!hasFile) return o;
          const updatedFiles = (o.files || []).map((f: any) => {
            if (f.id !== file.id) return f;
            return {
              ...f,
              printOptions: {
                ...(f.printOptions || f.print_options || {}),
                duplex: nextDuplex,
              },
              print_options: {
                ...(f.print_options || f.printOptions || {}),
                duplex: nextDuplex,
              },
            };
          });
          return { ...o, files: updatedFiles };
        })
      );

      try {
        await api.updateFileOptions(file.id, { duplex: nextDuplex }, effectiveShopId);
        showToast('Duplex Updated', `${file.fileName || 'Document'} set to ${nextDuplex ? '2-Sided' : '1-Sided'}`, 'green');
        await refreshQueue();
      } catch (err: any) {
        showToast('Error', err.message, 'red');
        await refreshQueue();
      }
    };

    const handleResetPrintingStatus = async (orderId: string) => {
      try {
        await api.resetPrintOrder(orderId, effectiveShopId);
        showToast('Print State Reset', 'Order returned to Ready state. Previous print canceled.', 'amber');
        await refreshQueue();
      } catch (err: any) {
        showToast('Error', err.message, 'red');
      }
    };

    const openAdvanceFileModal = (file: any, order: any) => {
      const currentOpts = file.printOptions || file.print_options || {};
      const orderDuplex = order.duplex !== false;
      const currentPages = file.pageCount ?? file.page_count ?? 1;
      setAdvanceModal({
        show: true, targetType: 'file', targetId: file.id,
        targetName: file.fileName || file.file_name || 'Document',
        options: {
          duplex: currentOpts.duplex !== undefined ? Boolean(currentOpts.duplex) : orderDuplex,
          copies: currentOpts.copies ?? order.copies ?? 1,
          pageRange: currentOpts.pageRange ?? '',
          pageCount: currentPages,
          colorMode: currentOpts.colorMode ?? ((order.colorMode === 'COLOR') ? 'COLOR' : 'BW'),
          orientation: currentOpts.orientation ?? 'auto',
          fitToPage: currentOpts.fitToPage !== undefined ? Boolean(currentOpts.fitToPage) : true,
        },
      });
    };

    const openAdvanceOrderModal = (order: any) => {
      const firstFile = order.files?.[0];
      const currentOpts = firstFile?.printOptions || firstFile?.print_options || {};
      setAdvanceModal({
        show: true,
        targetType: 'order',
        targetId: order.id,
        targetName: `Order #${order.queueNumber ?? order.id.slice(0, 8)}`,
        options: {
          duplex: order.duplex !== false,
          copies: order.copies || 1,
          pageRange: currentOpts.pageRange ?? '',
          pageCount: firstFile?.pageCount ?? 1,
          colorMode: order.colorMode === 'COLOR' ? 'COLOR' : 'BW',
          orientation: currentOpts.orientation ?? 'auto',
          fitToPage: currentOpts.fitToPage !== undefined ? Boolean(currentOpts.fitToPage) : true,
        },
      });
    };

    const handleSaveAdvanceModal = async () => {
      if (!advanceModal.targetId) return;
      try {
        const safeDocPages = Math.max(1, Number(advanceModal.options.pageCount) || 1);
        const safePageRange = clampPageRange(advanceModal.options.pageRange, safeDocPages);

        if (advanceModal.targetType === 'file') {
          await api.updateFileOptions(advanceModal.targetId, {
            ...advanceModal.options,
            pageRange: safePageRange,
            pageCount: safeDocPages,
          }, effectiveShopId);
        } else {
          await api.updateOrderOptions(advanceModal.targetId, {
            duplex: advanceModal.options.duplex,
            copies: advanceModal.options.copies,
            colorMode: advanceModal.options.colorMode,
            pageRange: safePageRange,
            pageCount: safeDocPages,
            orientation: advanceModal.options.orientation,
            fitToPage: advanceModal.options.fitToPage,
          }, effectiveShopId);
        }
        showToast('Saved', `Print rules updated for ${advanceModal.targetName}`, 'green');
        setAdvanceModal((p) => ({ ...p, show: false }));
        await refreshQueue();
      } catch (err: any) { showToast('Error', err.message, 'red'); }
    };

    const handleUnlockAdmin = (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      if (adminKey.trim() === 'admin_print_shop_master_2026' || adminKey.trim().length > 5) {
        setIsAdminUnlocked(true);
        showToast('Admin Unlocked', 'Configuration access granted.', 'green');
      } else {
        showToast('Access Denied', 'Invalid master admin key.', 'red');
      }
    };

    const handleSaveAdminSettings = async () => {
      if (!effectiveShopId) { showToast('Error', 'Shop ID required', 'red'); return; }
      try {
        if (adminConfig.accessToken) {
          await api.saveWhatsAppCredentials(effectiveShopId, {
            phoneNumberId: adminConfig.phoneId, wabaId: adminConfig.wabaId,
            verifyToken: adminConfig.verifyToken, appSecret: 'secret_print_bridge_2026',
            accessToken: adminConfig.accessToken,
          });
        }
        showToast('Settings Saved', 'Global system parameters updated.', 'green');
        await loadDiagnostics();
      } catch (err: any) { showToast('Error', err.message, 'red'); }
    };

    const handleTestWhatsApp = async () => {
      setTestingWa(true); setWaTestResult(null);
      try {
        let res;
        if (adminConfig.accessToken && effectiveShopId) {
          await api.saveWhatsAppCredentials(effectiveShopId, { phoneNumberId: adminConfig.phoneId, wabaId: adminConfig.wabaId, verifyToken: adminConfig.verifyToken, appSecret: 'secret_print_bridge_2026', accessToken: adminConfig.accessToken });
          res = await api.testWhatsAppCredentials(effectiveShopId, { phoneNumberId: adminConfig.phoneId, wabaId: adminConfig.wabaId, verifyToken: adminConfig.verifyToken, appSecret: 'secret_print_bridge_2026', accessToken: adminConfig.accessToken });
        } else {
          res = await api.testShopWhatsAppConnection(effectiveShopId);
        }
        setWaTestResult(res);
        await loadDiagnostics();
      } catch (err: any) { setWaTestResult({ ok: false, error: err.message || 'Connection test failed' }); }
      finally { setTestingWa(false); }
    };

    // =====  SUBSCRIPTION LOCKOUT  =====
    if ((shop?.status as string) === 'EXPIRED' || (shop?.status as string) === 'SUSPENDED') {
      return (
        <div className="max-w-2xl mx-auto mt-20 p-8 glass-card rounded-2xl border border-rose-500/25 text-center space-y-6 animate-scale-in">
          <div className="w-20 h-20 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 mx-auto flex items-center justify-center">
            <ShieldAlert className="w-10 h-10" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Subscription {shop?.status}</h2>
            <p className="text-slate-400 text-sm mt-2">WhatsApp Print Bridge is paused until your subscription is renewed.</p>
          </div>
          <button onClick={() => refreshProfile()} className="btn-primary inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Check Status
          </button>
        </div>
      );
    }

    const top10Orders = orders.slice(0, 10);
    const totalCachedFiles = top10Orders.reduce((sum, o) => sum + (o.files?.length || 0), 0);

    // =====  TOAST  =====
    const Toast = () => (
      <div className={`fixed bottom-6 right-6 z-50 transition-all duration-300 max-w-sm w-full ${toast.show ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0 pointer-events-none'}`}>
        <div className={`glass-modal p-4 rounded-xl flex items-start gap-3 border ${toast.color === 'green' ? 'border-emerald-500/30' : toast.color === 'amber' ? 'border-amber-500/30' : 'border-rose-500/30'}`}>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm flex-shrink-0 ${toast.color === 'green' ? 'bg-emerald-500/15 text-emerald-400' : toast.color === 'amber' ? 'bg-amber-500/15 text-amber-400' : 'bg-rose-500/15 text-rose-400'}`}>
            {toast.color === 'green' ? '✓' : toast.color === 'amber' ? '!' : '✕'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-white">{toast.title}</div>
            <div className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">{toast.message}</div>
          </div>
        </div>
      </div>
    );

    // =====  TAB BAR  =====
    const TabBar = () => (
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div className="tab-bar flex-wrap">
          {[
            { id: 'queue', label: 'Live Queue', icon: <Printer className="w-3.5 h-3.5" />, count: orders.filter((o: any) => { const exp = o.expiresAt || o.expires_at; const isLive = o.status === 'PENDING' || o.status === 'PRINTING'; return isLive && (!exp || new Date(exp).getTime() > Date.now()); }).length },
            { id: 'messages', label: 'WhatsApp Messages', icon: <MessageSquare className="w-3.5 h-3.5" /> },
            { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="w-3.5 h-3.5" /> },
            { id: 'settings', label: 'Settings', icon: <Settings className="w-3.5 h-3.5" /> },
          ].map((tab) => (
            <button
              key={tab.id}
              id={`tabBtn-${tab.id}`}
              onClick={() => changeTab(tab.id)}
              className={`tab-item ${currentTab === tab.id ? (tab.id === 'settings' ? 'active-settings' : 'active') : ''}`}
            >
              {tab.icon}
              {tab.label}
              {tab.count !== undefined && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono font-bold ${currentTab === tab.id ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/08 text-slate-500'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/08 border border-emerald-500/15 text-emerald-400 text-[11px] font-medium">
          <Sparkles className="w-3 h-3" />
          <span>Top 10 Buffer: {totalCachedFiles} files pre-cached</span>
        </div>
      </div>
    );

    // ==========================================================================
    // TAB 1: LIVE PRINT QUEUE
    // ==========================================================================
    if (currentTab === 'queue') {
      const enabledPrinters = printers.filter((p) => p.enabled);

      return (
        <div className="space-y-5 animate-fade-up">
          <TabBar />

          {/* Status Bar */}
          <div className="glass-card p-4 rounded-2xl flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Agent status */}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-medium ${diagnostics?.agent?.online ? 'status-online' : 'status-offline'}`}>
                <span className={`w-2 h-2 rounded-full ${diagnostics?.agent?.online ? 'bg-emerald-400 animate-pulse' : 'bg-rose-500'}`} />
                <span>Print Agent: {diagnostics?.agent?.online ? 'Online' : 'Offline'}</span>
              </div>

              {/* WhatsApp status */}
              <div
                onClick={() => setShowWaModal(true)}
                title={
                  baileysState?.status === 'CONNECTED'
                    ? `WhatsApp Linked Device: ${baileysState.phone || 'Active'} (100% Free Forever)`
                    : diagnostics?.whatsapp?.lastError ||
                    (diagnostics?.whatsapp?.isMetaApiReachable ? 'WhatsApp Connected' : 'WhatsApp Not Linked - Click to Scan QR')
                }
                className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-medium cursor-pointer transition-colors ${baileysState?.status === 'CONNECTED' || (diagnostics?.whatsapp?.configured && diagnostics?.whatsapp?.isMetaApiReachable)
                  ? 'status-online'
                  : baileysState?.status === 'SCAN_QR'
                    ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                    : diagnostics?.whatsapp?.lastError
                      ? 'status-offline'
                      : 'status-warning'
                  }`}
              >
                <span className={`w-2 h-2 rounded-full ${baileysState?.status === 'CONNECTED' || (diagnostics?.whatsapp?.configured && diagnostics?.whatsapp?.isMetaApiReachable)
                  ? 'bg-emerald-400 animate-pulse'
                  : baileysState?.status === 'SCAN_QR'
                    ? 'bg-amber-400 animate-pulse'
                    : diagnostics?.whatsapp?.lastError
                      ? 'bg-rose-500 animate-pulse'
                      : 'bg-amber-400'
                  }`} />
                <span>
                  WhatsApp: {
                    baileysState?.status === 'CONNECTED'
                      ? `Linked (${baileysState.phone || 'Free'})`
                      : baileysState?.status === 'SCAN_QR'
                        ? 'Scan QR Code'
                        : diagnostics?.whatsapp?.configured && diagnostics?.whatsapp?.isMetaApiReachable
                          ? `Connected (${diagnostics.whatsapp.verifiedName || 'Verified'})`
                          : diagnostics?.whatsapp?.lastError?.toLowerCase().includes('expired')
                            ? 'Token Expired'
                            : diagnostics?.whatsapp?.lastError
                              ? 'API Issue'
                              : 'Link Phone'
                  }
                </span>
              </div>

              {/* Active Printers Count */}
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-xl border border-white/08 text-xs font-medium text-slate-300">
                <Printer className="w-3.5 h-3.5 text-emerald-400" />
                <span>{enabledPrinters.length} Printer{enabledPrinters.length === 1 ? '' : 's'} Active (ON)</span>
              </div>

              {/* Webhook */}
              <div className="hidden xl:flex items-center gap-2 px-3 py-1.5 rounded-xl border border-white/08 text-xs font-medium text-slate-400">
                <Clock className="w-3 h-3" />
                <span>Last Webhook: {diagnostics?.webhook?.lastReceivedAt ? new Date(diagnostics.webhook.lastReceivedAt).toLocaleTimeString() : 'Awaiting...'}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => refreshQueue()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
            </div>
          </div>

          {/* Token Expiry Alert Banner */}
          {diagnostics?.whatsapp?.lastError && (
            <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/25 flex items-start gap-3 text-xs text-amber-200 animate-fade-in">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 space-y-1">
                <div className="font-bold text-amber-300">
                  Meta WhatsApp Access Token Expired (24h Test Sandbox Limit)
                </div>
                <p className="text-[11px] text-amber-200/80 leading-relaxed">
                  Meta rejected downloading the real PDF bytes from WhatsApp because the developer test token expired. Please refresh your Access Token in <strong>Settings &gt; WhatsApp Integration</strong>.
                </p>
              </div>
            </div>
          )}

          {/* Queue Header & Live/Archive Selector */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-1">
            <div>
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                {queueFilter === 'live' ? 'Active Print Queue' : 'Archived Orders'}
                {queueFilter === 'archived' && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                    Skipped 2×
                  </span>
                )}
              </h2>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {queueFilter === 'live'
                  ? 'Top 10 pre-buffered locally · Real-time WebSocket sync'
                  : 'Orders moved here after 2 skips to clear counter. Automatically reactivated when customer messages or arrives!'}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex rounded-xl bg-slate-900/80 p-1 border border-white/10 text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => setQueueFilter('live')}
                  className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${queueFilter === 'live'
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30 font-bold'
                    : 'text-slate-400 hover:text-white'
                    }`}
                >
                  <span>Live Queue</span>
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${queueFilter === 'live' ? 'bg-white/20 text-white' : 'bg-white/10 text-slate-400'
                    }`}>
                    {orders.filter((o: any) => { const exp = o.expiresAt || o.expires_at; const isLive = o.status === 'PENDING' || o.status === 'PRINTING'; return isLive && (!exp || new Date(exp).getTime() > Date.now()); }).length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => { setQueueFilter('archived'); loadArchivedQueue(); }}
                  className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${queueFilter === 'archived'
                    ? 'bg-amber-600 text-white shadow-md shadow-amber-600/30 font-bold'
                    : 'text-slate-400 hover:text-white'
                    }`}
                >
                  <RotateCcw className="w-3 h-3" />
                  <span>Archived</span>
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${queueFilter === 'archived' ? 'bg-white/20 text-white' : 'bg-amber-500/20 text-amber-300'
                    }`}>
                    {archivedOrders.length}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Orders List / Empty State */}
          {(() => {
            const liveOrders = orders.filter((o: any) => {
              const isLive = o.status === 'PENDING' || o.status === 'PRINTING';
              const exp = o.expiresAt || o.expires_at;
              return isLive && (!exp || new Date(exp).getTime() > Date.now());
            });
            const currentOrderList = queueFilter === 'live' ? liveOrders : archivedOrders;
            if (currentOrderList.length === 0) {
              return queueFilter === 'live' ? (
                <div className="py-24 glass-card rounded-2xl text-center space-y-4 border border-dashed border-white/10 animate-fade-in">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 mx-auto flex items-center justify-center">
                    <Printer className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Queue is Empty</h3>
                  <p className="text-sm text-slate-500 max-w-sm mx-auto leading-relaxed">
                    No pending print orders. New WhatsApp documents appear here instantly.
                  </p>
                </div>
              ) : (
                <div className="py-24 glass-card rounded-2xl text-center space-y-3 border border-dashed border-amber-500/20 animate-fade-in">
                  <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400 mx-auto flex items-center justify-center">
                    <RotateCcw className="w-7 h-7" />
                  </div>
                  <h3 className="text-base font-bold text-white">No Archived Orders</h3>
                  <p className="text-xs text-slate-400 max-w-sm mx-auto leading-relaxed">
                    Orders skipped 2 times using "Turn Next" will appear here to keep your live counter moving smoothly.
                  </p>
                </div>
              );
            }

            return (
              <div className="space-y-3">
                {currentOrderList.map((order: any, index: number) => {
                  const rank = index + 1;
                  const isTop10 = rank <= 10;
                  const files = order.files || [];
                  const printStatus = order.printStatus || (order as any).print_status || 'NONE';
                  const lastPrinter = order.lastPrinterName || (order as any).last_printer_name;
                  const nonRevokedFiles = files.filter((f: any) => f.status !== 'REVOKED');
                  const isAllFilesPrinted = nonRevokedFiles.length > 0 && nonRevokedFiles.every((f: any) => f.status === 'PRINTED');
                  const isPrintDone = printStatus === 'PRINT_DONE' || isAllFilesPrinted;
                  const isPrintFailed = printStatus === 'FAILED';
                  const isPrinting = (order.status === 'PRINTING' || printStatus === 'PRINTING') && !isPrintDone && !isPrintFailed;

                  const directFiles = nonRevokedFiles.filter((f: any) => {
                    const ext = (f.extension || f.fileName || '').toLowerCase();
                    const routeType = f.routeType || f.route_type;
                    return routeType === 'DIRECT' || (ext.endsWith('.pdf') && routeType !== 'MANUAL');
                  });
                  const isAllManual = directFiles.length === 0 && nonRevokedFiles.length > 0;
                  const hasPrintedOnce =
                    isPrintDone ||
                    isPrinting ||
                    (order.printStatus || (order as any).print_status) === 'PRINT_DONE' ||
                    (order.printStatus || (order as any).print_status) === 'PRINTING' ||
                    nonRevokedFiles.some((f: any) => f.status === 'PRINTED' || f.status === 'PRINTING');

                  const hasManualFiles = nonRevokedFiles.some((f: any) => {
                    const ext = (f.extension || f.fileName || '').toLowerCase();
                    const routeType = f.routeType || f.route_type;
                    return routeType === 'MANUAL' || !ext.endsWith('.pdf');
                  });

                  let remainingHours = 23; let remainingMins = 0;
                  const expiresAtVal = order.expiresAt || order.expires_at;
                  if (expiresAtVal) {
                    const diffMs = Math.max(0, new Date(expiresAtVal as string | number | Date).getTime() - Date.now());
                    remainingHours = Math.floor(diffMs / 3600000);
                    remainingMins = Math.floor((diffMs % 3600000) / 60000);
                  }

                  const bill = calculateOrderBill(order);
                  const { prefix, last3 } = formatCustomerPhone(String((order as any).customerPhone || order.phone || ''));

                  const isShopSubscriber = Boolean(order.isSubscriber && order.subscriberType === 'SHOP');

                  const cardBorderClass = isPrintDone
                    ? 'border-2 border-emerald-400 bg-gradient-to-r from-emerald-950/50 via-emerald-900/30 to-emerald-950/50 shadow-2xl shadow-emerald-950/70 ring-2 ring-emerald-500/50'
                    : isPrinting
                      ? 'border-2 border-cyan-400/50 bg-cyan-950/20 ring-2 ring-cyan-500/30'
                      : isShopSubscriber
                        ? 'border-2 border-amber-400/60 bg-gradient-to-r from-amber-950/40 via-amber-900/20 to-slate-900/40 shadow-xl shadow-amber-950/40 ring-1 ring-amber-400/30'
                        : isPrintFailed
                          ? 'border-2 border-rose-500/50 bg-rose-950/25 ring-2 ring-rose-500/30'
                          : isTop10
                            ? 'border-emerald-500/20'
                            : 'border-white/06';

                  return (
                    <div
                      key={order.id}
                      className={`order-card glass-card rounded-2xl border transition-all duration-300 ${cardBorderClass}`}
                    >
                      {/* Card Header */}
                      <div className="p-4 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {/* Rank badge: dynamic queue position (#1 for top order, #2 for next, etc.) */}
                          <div className={`rank-badge ${isTop10 ? 'top' : 'regular'}`} title={`Queue Position: #${rank} in line`}>#{rank}</div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-sm text-slate-300 font-mono">{prefix}</span>
                              {last3 && (
                                <span
                                  className="text-xl md:text-2xl font-black text-emerald-300 bg-emerald-500/20 px-2.5 py-0.5 rounded-xl border border-emerald-500/40 shadow-sm font-mono tracking-widest inline-flex items-center"
                                  title={`Customer Token: ${last3} — Ask customer for last 3 digits`}
                                >
                                  {last3}
                                </span>
                              )}
                              <span className="text-[11px] font-mono text-slate-500">({order.id.slice(0, 8)})</span>
                              {isShopSubscriber && (
                                <span
                                  className="text-xs px-2.5 py-0.5 rounded-full bg-gradient-to-r from-amber-500/25 to-yellow-500/25 text-amber-300 font-bold border border-amber-500/40 flex items-center gap-1 shadow-sm font-sans"
                                  title={order.subscriberLabel ? `Priority Subscribed Client: ${order.subscriberLabel}` : 'Priority Subscribed Number'}
                                >
                                  <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                                  <span>⭐ Subscribed Client</span>
                                  {order.subscriberLabel && <span className="text-[10px] text-amber-200/80 font-normal">({order.subscriberLabel})</span>}
                                </span>
                              )}
                              {isTop10 && <span className="text-xs px-2.5 py-0.5 rounded-full badge-cached font-mono font-semibold">⚡ Cached</span>}
                              {isPrintDone && (
                                <span className="text-xs px-3 py-1 rounded-full bg-emerald-500/30 text-emerald-200 font-black border border-emerald-400 flex items-center gap-1.5 shadow-md animate-pulse">
                                  <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                                  PRINT COMPLETED ✅ {lastPrinter ? `(${lastPrinter})` : ''} — READY TO CONFIRM & COLLECT
                                </span>
                              )}
                              {isPrinting && (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 font-bold border border-cyan-500/40 flex items-center gap-1.5 shadow-sm">
                                    <span className="w-2 h-2 rounded-full bg-cyan-400 animate-ping"></span>
                                    PRINTING IN PROGRESS... {lastPrinter ? `on ${lastPrinter}` : ''}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => openPrinterSelectModal(order, false, true, undefined, rank)}
                                    className="text-xs px-2.5 py-1 rounded-xl bg-amber-500/25 hover:bg-amber-500/35 text-amber-200 border border-amber-500/40 font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-md shadow-amber-950/40"
                                    title="Printer stuck or wrong printer? Click to select a new printer and automatically cancel previous spooling"
                                  >
                                    <Printer className="w-3.5 h-3.5 text-amber-300" />
                                    Switch Printer 🖨️
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleResetPrintingStatus(order.id)}
                                    className="text-xs px-2.5 py-1 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/35 font-semibold flex items-center gap-1 transition-all cursor-pointer"
                                    title="Cancel print state and return order to Ready"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                    Cancel
                                  </button>
                                </div>
                              )}
                              {isPrintFailed && (
                                <span className="text-xs px-2.5 py-0.5 rounded-full bg-rose-500/25 text-rose-300 font-bold border border-rose-500/40 flex items-center gap-1.5 shadow-sm">
                                  <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
                                  PRINT FAILED ⚠️ (Click "Print Again" to retry)
                                </span>
                              )}
                              {((order.turnNextCount || (order as any).turn_next_count) ?? 0) > 0 && (
                                <span className={`text-xs px-2.5 py-0.5 rounded-full font-bold border ${order.status === 'ARCHIVED'
                                  ? 'bg-amber-500/25 text-amber-300 border-amber-500/40 animate-pulse'
                                  : 'bg-amber-500/15 border border-amber-500/30 text-amber-300'
                                  }`}>
                                  {order.status === 'ARCHIVED'
                                    ? `Archived (${order.turnNextCount || (order as any).turn_next_count}× Skips)`
                                    : `Skipped ${order.turnNextCount || (order as any).turn_next_count}×`}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-400 font-mono mt-1 flex items-center gap-3">
                              <span>{files.length} file(s) attached</span>
                              <span>•</span>
                              <span className="text-slate-300 font-semibold">{order.duplex !== false ? '2-Sided (Default)' : '1-Sided (Default)'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Actions Row - Touch Friendly Large Buttons */}
                        <div className="flex flex-wrap items-center gap-2.5">
                          {/* Auto-Calculated Bill Badge */}
                          <div className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-emerald-500/15 border border-emerald-500/35 text-emerald-300 font-extrabold text-sm md:text-base font-mono shadow-sm">
                            <IndianRupee className="w-4 h-4 text-emerald-400" />
                            <span className="text-xs text-emerald-400/90 font-sans font-medium">Collect:</span>
                            <span>₹{bill}</span>
                          </div>

                          {/* Expiry */}
                          <div className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border font-mono text-xs ${remainingHours < 2 ? 'border-amber-500/30 text-amber-400 bg-amber-500/08' : 'border-white/08 text-slate-400'}`}>
                            <Clock className="w-3.5 h-3.5" />
                            {remainingHours}h {remainingMins}m
                          </div>

                          {/* Copies Stepper */}
                          <div className="flex items-center rounded-xl bg-black/30 border border-white/10 p-0.5">
                            <button
                              type="button"
                              onClick={() => api.updateOrderOptions(order.id, { copies: Math.max(1, (order.copies || 1) - 1) }, effectiveShopId)}
                              className="w-8 h-8 rounded-lg bg-white/05 hover:bg-white/12 text-slate-300 flex items-center justify-center transition-all"
                              title="Decrease copies"
                            >
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="px-2.5 font-bold text-xs text-white font-mono min-w-[30px] text-center">
                              {order.copies || 1}x
                            </span>
                            <button
                              type="button"
                              onClick={() => api.updateOrderOptions(order.id, { copies: (order.copies || 1) + 1 }, effectiveShopId)}
                              className="w-8 h-8 rounded-lg bg-white/05 hover:bg-white/12 text-slate-300 flex items-center justify-center transition-all"
                              title="Increase copies"
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Master Duplex Toggle (Cascades to all files) */}
                          <button
                            type="button"
                            onClick={() => handleToggleMasterDuplex(order)}
                            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold border transition-all ${order.duplex !== false
                              ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300 shadow-sm'
                              : 'bg-white/05 border-white/10 text-slate-400 hover:text-white'
                              }`}
                            title="Master Toggle: Changes double/single side for ALL files in this order"
                          >
                            <Layers className="w-4 h-4" />
                            {order.duplex !== false ? 'All Double-Sided' : 'All Single-Sided'}
                          </button>

                          {/* Master Color Toggle (Cascades to all files) */}
                          <button
                            type="button"
                            onClick={() => handleToggleMasterColor(order)}
                            disabled={!pricingConfig.colorPrintEnabled}
                            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold border transition-all ${!pricingConfig.colorPrintEnabled
                              ? 'opacity-40 cursor-not-allowed bg-white/03 border-white/06 text-slate-500'
                              : (order.colorMode || order.color_mode) === 'COLOR'
                                ? 'bg-purple-500/15 border-purple-500/30 text-purple-300 shadow-sm'
                                : 'bg-white/05 border-white/10 text-slate-400 hover:text-white'
                              }`}
                            title={pricingConfig.colorPrintEnabled ? 'Master Toggle: B&W or Colour' : 'Color Printing is turned OFF in Settings'}
                          >
                            <Palette className="w-4 h-4" />
                            {!pricingConfig.colorPrintEnabled
                              ? 'B&W Only'
                              : (order.colorMode || order.color_mode) === 'COLOR'
                                ? 'Colour'
                                : 'B&W'}
                          </button>

                          {/* Turn next / Restore */}
                          {order.status === 'ARCHIVED' ? (
                            <button
                              type="button"
                              onClick={() => handleUnarchiveOrder(order.id)}
                              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold text-white bg-amber-600 hover:bg-amber-500 shadow-md shadow-amber-600/30 transition-all cursor-pointer"
                              title="Restore order back to active live queue"
                            >
                              <RotateCcw className="w-3.5 h-3.5" /> Restore to Queue
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleTurnNextOrder(order.id)}
                              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-indigo-300 bg-indigo-500/10 border border-indigo-500/20 hover:bg-indigo-500/20 transition-all cursor-pointer"
                              title="Move to end of queue (Archived after 2 skips)"
                            >
                              <RotateCcw className="w-3.5 h-3.5" /> Turn Next
                            </button>
                          )}

                          {/* Advance options */}
                          <button
                            type="button"
                            onClick={() => openAdvanceOrderModal(order)}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 hover:bg-cyan-500/20 transition-all"
                          >
                            <Sliders className="w-3.5 h-3.5" /> Options
                          </button>

                          {/* Print / Re-Print Button (Opens Printer Selection Modal) */}
                          <button
                            type="button"
                            onClick={() => {
                              if (files.length === 0) {
                                showToast('No Files Attached', 'Waiting for document from customer.', 'amber');
                                return;
                              }
                              openPrinterSelectModal(order, isAllManual, isPrintDone || isPrinting, undefined, rank);
                            }}
                            disabled={files.length === 0}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white transition-all cursor-pointer ${files.length === 0
                              ? 'bg-slate-700/50 text-slate-500 cursor-not-allowed border border-white/05'
                              : isPrintDone
                                ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 shadow-lg shadow-cyan-600/30 ring-1 ring-cyan-400/40'
                                : isPrinting
                                  ? 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 shadow-lg shadow-amber-600/25'
                                  : 'bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 shadow-lg shadow-emerald-600/25'
                              }`}
                            title={
                              files.length === 0
                                ? 'No files attached in this order'
                                : isPrintDone
                                  ? 'All files printed! Click to reprint on this or another printer if needed.'
                                  : isPrinting
                                    ? 'Printing in progress. Click to switch printer or re-print.'
                                    : isAllManual
                                      ? 'Save image files to Desktop for manual printing'
                                      : 'Select printer and print'
                            }
                          >
                            <Printer className="w-4 h-4" />
                            {files.length === 0
                              ? 'Awaiting Files'
                              : isPrintDone
                                ? 'Print Again 🖨️'
                                : isPrinting
                                  ? 'Switch / Re-Print 🖨️'
                                  : isPrintFailed
                                    ? 'Retry Print ⚠️'
                                    : isAllManual
                                      ? 'Export to Desktop 📂'
                                      : 'Print Now 🖨️'}
                          </button>

                          {/* Confirm Done & Collect Bill Button (Requires printing at least once!) */}
                          <button
                            type="button"
                            disabled={!hasPrintedOnce}
                            onClick={() => {
                              if (!hasPrintedOnce) {
                                showToast('Print Required First', 'Please click "Print" to print documents before confirming handover!', 'amber');
                                return;
                              }
                              handleConfirmOrderDone(order, bill);
                            }}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${!hasPrintedOnce
                              ? 'opacity-40 cursor-not-allowed bg-white/05 border border-white/10 text-slate-500'
                              : isPrintDone
                                ? 'text-white bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 shadow-xl shadow-emerald-500/40 ring-2 ring-emerald-300 animate-pulse text-sm cursor-pointer'
                                : isPrinting
                                  ? 'text-white bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-500 hover:to-emerald-600 shadow-lg shadow-green-500/35 ring-1 ring-emerald-400/50 cursor-pointer'
                                  : 'text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 cursor-pointer'
                              }`}
                            title={
                              !hasPrintedOnce
                                ? 'Print documents first! Confirm button is enabled after printing.'
                                : 'Customer paid and collected prints? Click to complete order, notify customer on WhatsApp & record profit!'
                            }
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            {isPrintDone ? `Confirm Done & Handover (₹${bill})` : `Confirm Done (₹${bill})`}
                          </button>

                          {/* Purge */}
                          <button
                            type="button"
                            onClick={() => handleCancelOrder(order.id)}
                            className="w-9 h-9 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 flex items-center justify-center transition-all"
                            title="Purge order and clear files"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Files List */}
                      <div className="px-4 pb-4 space-y-2.5">
                        {files.map((file: any) => {
                          const fileName = file.fileName || file.file_name || 'document.pdf';
                          const cleanExt = (file.extension || fileName.slice(fileName.lastIndexOf('.')) || 'pdf').toLowerCase().replace(/^\./, '');
                          const ext = cleanExt.toUpperCase();
                          const routeType = file.routeType || file.route_type;
                          const isManual = ext !== 'PDF' || routeType === 'MANUAL';
                          const pageCount = file.pageCount ?? file.page_count ?? 1;
                          const cacheState = file.cacheState || file.cache_state;
                          const fileOpts = file.printOptions || file.print_options || {};

                          // Hierarchy: file override if explicitly specified, otherwise order duplex
                          const isFileDuplex = fileOpts.duplex !== undefined ? Boolean(fileOpts.duplex) : order.duplex !== false;
                          const hasExplicitOverride = fileOpts.duplex !== undefined && Boolean(fileOpts.duplex) !== (order.duplex !== false);
                          const effectiveCopies = fileOpts.copies !== undefined && Number(fileOpts.copies) >= 1 ? Number(fileOpts.copies) : (order.copies || 1);
                          const isFileColor = (fileOpts.colorMode ?? (order.colorMode || order.color_mode)) === 'COLOR' && pricingConfig.colorPrintEnabled;
                          const rawFilePages = Math.max(1, pageCount || 1);
                          const safeDisplayRange = clampPageRange(fileOpts.pageRange, rawFilePages);
                          const effectivePages = countPagesInRange(safeDisplayRange, rawFilePages);

                          const extBadgeClass = isManual ? 'badge-manual' : 'badge-pdf';

                          // Individual file cost calculation: 2-sided pairs use double-rate, odd leftover page uses single-rate
                          const fileCost = calculateFilePrice(
                            effectivePages,
                            effectiveCopies,
                            isFileDuplex,
                            isFileColor,
                            pricingConfig
                          );
                          const isFilePrinted = file.status === 'PRINTED';
                          const isFilePrinting = file.status === 'PRINTING';

                          return (
                            <div key={file.id} className={`flex flex-wrap items-center justify-between gap-4 p-4 rounded-2xl bg-black/30 border transition-all shadow-sm ${isFilePrinted ? 'border-emerald-500/30 bg-emerald-950/10' : isFilePrinting ? 'border-cyan-500/35 bg-cyan-950/15' : 'border-white/08 hover:border-white/16'}`}>
                              <div className="flex items-center gap-3.5 min-w-0 flex-1">
                                <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold font-mono text-xs flex-shrink-0 shadow-sm ${extBadgeClass}`}>
                                  {ext}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="font-semibold text-slate-100 text-sm flex items-center gap-2 flex-wrap">
                                    <span className="truncate max-w-[280px] md:max-w-[420px]" title={fileName}>{fileName}</span>
                                    <span className={`text-[10px] px-2 py-0.5 rounded font-mono ${isManual ? 'badge-manual' : 'badge-pdf'}`}>
                                      {isManual ? 'Manual Print' : 'Direct Print'}
                                    </span>
                                    {hasExplicitOverride && (
                                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 font-mono font-bold" title="This file has individual custom settings differing from the order">
                                        CUSTOM
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-slate-400 font-mono mt-1.5 flex items-center gap-2.5 flex-wrap">
                                    {/* Page Count Badge (Read-Only Total Document Pages) */}
                                    <div
                                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 font-bold"
                                      title={safeDisplayRange ? `Printing ${effectivePages} of ${rawFilePages} total pages` : `Document has ${rawFilePages} page(s)`}
                                    >
                                      <FileText className="w-3.5 h-3.5 text-emerald-400" />
                                      <span className="font-mono">
                                        {safeDisplayRange && effectivePages !== rawFilePages
                                          ? `${effectivePages} of ${rawFilePages} page(s)`
                                          : `${rawFilePages} page(s)`}
                                      </span>
                                    </div>
                                    <span>•</span>
                                    <span className="text-slate-300">{effectiveCopies} {effectiveCopies === 1 ? 'copy' : 'copies'}</span>
                                    {!isManual && (
                                      <>
                                        <span>•</span>
                                        <span className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${isFileDuplex && effectivePages > 1
                                          ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300'
                                          : 'bg-cyan-500/15 border-cyan-500/30 text-cyan-300'
                                          }`}>
                                          {effectivePages <= 1 ? '1-Sided (1 pg)' : (isFileDuplex ? '2-Sided' : '1-Sided')}
                                        </span>
                                      </>
                                    )}
                                    <span>•</span>
                                    {isManual ? (
                                      <span className="text-amber-400 font-medium text-[11px] bg-amber-500/10 border border-amber-500/25 px-2 py-0.5 rounded" title="Manual non-PDF documents are excluded from automated bill">
                                        Manual Print (₹0)
                                      </span>
                                    ) : (
                                      <span className="text-emerald-300 font-bold text-xs bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 rounded">
                                        ₹{fileCost}
                                      </span>
                                    )}
                                    {safeDisplayRange && (
                                      <span className="text-cyan-300 font-bold bg-cyan-500/15 border border-cyan-500/30 px-2 py-0.5 rounded text-[11px]">
                                        Pages: {safeDisplayRange} ({effectivePages} pg)
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Sequenced Action Buttons */}
                              <div className="flex items-center gap-2.5 flex-wrap justify-end">
                                {isFilePrinted && (
                                  <span className="text-emerald-300 font-bold flex items-center gap-1 text-xs font-mono px-2.5 py-1.5 rounded-lg bg-emerald-500/20 border border-emerald-500/40 shadow-sm">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Printed ✅
                                  </span>
                                )}
                                {isFilePrinting && (
                                  <span className="text-cyan-300 font-bold flex items-center gap-1 text-xs font-mono animate-pulse px-2.5 py-1.5 rounded-lg bg-cyan-500/20 border border-cyan-500/40">
                                    <Printer className="w-3.5 h-3.5 animate-spin" /> Printing...
                                  </span>
                                )}
                                {cacheState === 'CACHED' && !isFilePrinted && !isFilePrinting && (
                                  <span className="text-emerald-400 flex items-center gap-1 text-xs font-mono px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Cached
                                  </span>
                                )}
                                {cacheState === 'DOWNLOADING' && (
                                  <span className="text-amber-400 flex items-center gap-1 text-xs font-mono animate-pulse px-2.5 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                                    <Download className="w-3.5 h-3.5" /> Buffering
                                  </span>
                                )}

                                {/* 1. Duplex Toggle (Both Side / Single Side) */}
                                {!isManual && (
                                  <button
                                    type="button"
                                    onClick={() => handleToggleFileDuplex(file, isFileDuplex)}
                                    className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer shadow-sm ${isFileDuplex && effectivePages > 1
                                      ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30'
                                      : 'bg-cyan-500/15 border-cyan-500/35 text-cyan-300 hover:bg-cyan-500/25'
                                      }`}
                                    title={effectivePages <= 1 ? '1 page document: Prints single-sided' : 'Click to toggle Single / Both sides for ONLY this file'}
                                  >
                                    <Layers className="w-4 h-4" />
                                    <span>{effectivePages <= 1 ? 'Single Side (1 pg)' : (isFileDuplex ? 'Both Side' : 'Single Side')}</span>
                                  </button>
                                )}

                                {/* 2. Print / Export File */}
                                {isManual ? (
                                  <button
                                    type="button"
                                    onClick={() => openPrinterSelectModal(order, true, false, file.id, rank)}
                                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition-all cursor-pointer shadow-sm"
                                    title="Export image/document to Desktop/Manual_Print"
                                  >
                                    <Download className="w-4 h-4 text-amber-400" />
                                    <span>Save to Desktop</span>
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => openPrinterSelectModal(order, false, isFilePrinted || isPrinting, file.id, rank)}
                                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer shadow-sm ${isFilePrinted
                                      ? 'text-cyan-200 bg-cyan-500/20 border-cyan-500/35 hover:bg-cyan-500/30'
                                      : 'text-emerald-200 bg-emerald-500/20 border border-emerald-500/35 hover:bg-emerald-500/30'
                                      }`}
                                    title={isFilePrinted ? 'This file was already printed. Click to reprint on any printer.' : 'Print this specific file directly'}
                                  >
                                    <Printer className="w-4 h-4 text-emerald-300" />
                                    <span>{isFilePrinted ? 'Reprint File 🖨️' : isFilePrinting ? 'Switch Printer / Print 🖨️' : 'Print File'}</span>
                                  </button>
                                )}

                                {/* 3. Adv Settings */}
                                {!isManual && (
                                  <button
                                    type="button"
                                    onClick={() => openAdvanceFileModal(file, order)}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-slate-300 bg-white/06 border border-white/12 hover:bg-white/12 hover:text-white transition-all cursor-pointer"
                                    title="Configure page range, orientation, and copies for this document"
                                  >
                                    <Sliders className="w-3.5 h-3.5 text-slate-400" />
                                    <span>Adv</span>
                                  </button>
                                )}

                                {/* 4. Delete File (Single clean action button) */}
                                <button
                                  type="button"
                                  onClick={async () => {
                                    await api.removeFile(file.id, effectiveShopId);
                                    showToast('File Deleted', `${fileName} removed from order`, 'amber');
                                    await refreshQueue();
                                  }}
                                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-rose-300 bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500/20 hover:text-rose-200 transition-all cursor-pointer"
                                  title="Delete this file from customer order"
                                >
                                  <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                                  <span>Delete</span>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* MODAL: Select Destination Printer */}
          {printerSelectModal.show && createPortal(
            <div className="modal-overlay" onClick={() => setPrinterSelectModal((p) => ({ ...p, show: false }))}>
              <div className="glass-modal max-w-xl sm:max-w-2xl w-full max-h-[90vh] overflow-y-auto my-auto p-6 rounded-3xl border border-emerald-500/30 shadow-2xl space-y-5 animate-scale-in" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-white/08 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 flex-shrink-0">
                      <Printer className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-white">
                        {printerSelectModal.isReprint ? 'Select Destination Printer (Switch / Re-Print)' : 'Choose Which Printer to Print On'}
                      </h3>
                      <p className="text-xs text-slate-400">
                        Order #{printerSelectModal.orderNumber || printerSelectModal.orderId.slice(0, 8)} · {printerSelectModal.fileCount} file(s) · Collect: ₹{printerSelectModal.totalBill}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPrinterSelectModal((p) => ({ ...p, show: false }))}
                    className="w-8 h-8 rounded-xl bg-white/06 hover:bg-white/12 text-slate-400 hover:text-white flex items-center justify-center transition-all flex-shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Bill preview banner */}
                <div className="p-3.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-emerald-400" />
                    <span className="text-xs text-slate-300 font-medium">Estimated Bill to Collect:</span>
                  </div>
                  <span className="text-base font-extrabold text-emerald-300 font-mono">₹{printerSelectModal.totalBill}</span>
                </div>

                {/* List of active (turned ON) printers */}
                <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center justify-between">
                    <span>Active Printers (Turned ON)</span>
                    <span className="text-emerald-400 font-mono font-bold">
                      {printers.filter((p) => p.enabled).length} printer(s) ready
                    </span>
                  </div>

                  {printers.filter((p) => p.enabled).length === 0 ? (
                    <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/25 text-rose-300 text-xs text-center">
                      All printers are currently turned OFF in Settings! Please turn ON at least one printer.
                    </div>
                  ) : (
                    printers.filter((p) => p.enabled).map((p) => (
                      <div
                        key={p.id}
                        onClick={() => handleExecutePrint(p.id)}
                        className="p-4 rounded-2xl border border-white/10 hover:border-emerald-500/50 bg-black/30 hover:bg-emerald-500/10 cursor-pointer flex items-center justify-between gap-4 transition-all group shadow-sm hover:shadow-emerald-500/10"
                      >
                        <div className="flex items-center gap-3.5 min-w-0 flex-1">
                          <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center text-emerald-400 group-hover:scale-105 transition-transform flex-shrink-0">
                            <Printer className="w-5 h-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-bold text-sm text-white group-hover:text-emerald-300 transition-colors" title={p.name}>
                                {p.name}
                              </span>
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-mono font-semibold flex-shrink-0">
                                ● ON
                              </span>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono font-semibold flex-shrink-0 ${p.duplexSupported !== false
                                ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                                : 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30'
                                }`}>
                                {p.duplexSupported !== false ? '🔄 Auto-Duplex' : '📄 1-Sided'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-400 font-mono mt-1 flex-wrap">
                              <span className="flex-shrink-0">{p.type}</span>
                              {p.systemName && p.systemName !== p.name && (
                                <span className="text-slate-400" title={`Windows Real Driver: ${p.systemName}`}>
                                  • Win Driver: <strong className="text-slate-300">{p.systemName}</strong>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExecutePrint(p.id);
                          }}
                          className="btn-primary flex-shrink-0 whitespace-nowrap !py-2.5 !px-5 !text-xs font-bold flex items-center justify-center gap-1.5 shadow-md shadow-emerald-600/20 group-hover:shadow-emerald-500/40"
                        >
                          <Printer className="w-4 h-4 flex-shrink-0" />
                          <span>Print Here</span>
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {/* Cancel button */}
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => setPrinterSelectModal((p) => ({ ...p, show: false }))}
                    className="btn-secondary w-full py-2.5 text-xs text-slate-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* MODAL: Manual Drop Alert */}
          {manualAlertModal.show && createPortal(
            <div className="modal-overlay" onClick={() => setManualAlertModal({ ...manualAlertModal, show: false })}>
              <div className="glass-modal max-w-lg w-full max-h-[90vh] overflow-y-auto my-auto p-6 rounded-2xl border border-amber-500/30 space-y-4 animate-scale-in" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-white">Manual Print Files Saved to Desktop</h3>
                    <p className="text-xs text-slate-300 leading-relaxed mt-1">
                      Non-PDF files (Word, Excel, PPT, Photos, Images) require manual printing and have been automatically saved to your computer:
                    </p>
                  </div>
                </div>

                {manualAlertModal.fileName && (
                  <div className="bg-slate-900/70 border border-amber-500/25 rounded-xl p-3 flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/10 text-amber-400 shrink-0">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">File for Manual Print</div>
                      <div className="text-xs font-mono text-amber-300 break-all select-all font-medium mt-0.5" title={manualAlertModal.fileName}>
                        {manualAlertModal.fileName}
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-black/50 p-3 rounded-xl border border-white/10 text-xs font-mono text-emerald-400 select-all flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-base shrink-0">📁</span>
                    <span className="break-all">{manualAlertModal.path}</span>
                  </div>
                  <span className="text-[10px] text-slate-400 bg-white/5 px-2 py-0.5 rounded font-sans shrink-0 uppercase tracking-wider">Folder</span>
                </div>

                <div className="flex items-start gap-2.5 text-xs text-slate-300 bg-emerald-500/10 border border-emerald-500/20 p-3 rounded-xl leading-relaxed">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <span>
                    All <strong>PDF</strong> files are sent directly to your printer. Open the desktop folder above to view and manually print your Word documents, spreadsheets, slides, and photos.
                  </span>
                </div>

                <div className="flex justify-end pt-1">
                  <button onClick={() => setManualAlertModal({ ...manualAlertModal, show: false })} className="btn-primary">OK, Got It</button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* MODAL: Simulate Order */}
          {showSimulateModal && createPortal(
            <div className="modal-overlay" onClick={() => setShowSimulateModal(false)}>
              <div className="glass-modal max-w-md w-full max-h-[90vh] overflow-y-auto my-auto p-6 rounded-2xl border border-emerald-500/25 space-y-5 animate-scale-in" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-400" />
                    Simulate WhatsApp Customer Order
                  </h3>
                  <button onClick={() => setShowSimulateModal(false)} className="text-slate-500 hover:text-white p-1 rounded-lg transition-colors"><X className="w-4 h-4" /></button>
                </div>
                <form onSubmit={handleSimulateSubmit} className="space-y-4">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Customer Name</label>
                    <input type="text" value={simName} onChange={(e) => setSimName(e.target.value)} className="input-field" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Customer Phone</label>
                    <input type="text" value={simPhone} onChange={(e) => setSimPhone(e.target.value)} className="input-field font-mono" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Document Format</label>
                    <select value={simFileType} onChange={(e) => { const t = e.target.value as any; setSimFileType(t); setSimFileName(t === 'pdf' ? 'Conference.pdf' : t === 'docx' ? 'Report.docx' : 'Photo.png'); }} className="input-field">
                      <option value="pdf">PDF Document (.pdf) → Direct Print 🖨️</option>
                      <option value="docx">Word Document (.docx) → Download for Manual Print 📂</option>
                      <option value="image">Image / Photo (.png) → Download for Manual Print 📂</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">File Name</label>
                    <input type="text" value={simFileName} onChange={(e) => setSimFileName(e.target.value)} className="input-field font-mono" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Total Document Pages</label>
                    <input
                      type="number"
                      min="1"
                      max="999"
                      value={simPageCount}
                      onChange={(e) => setSimPageCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      className="input-field font-mono font-bold text-emerald-300"
                    />
                    <span className="text-[10px] text-slate-500 mt-1 block">Default: 12 pages for multi-page documents</span>
                  </div>
                  <div className="flex items-center justify-end gap-3 pt-1">
                    <button type="button" onClick={() => setShowSimulateModal(false)} className="btn-secondary">Cancel</button>
                    <button type="submit" disabled={isSimulating} className="btn-primary flex items-center gap-2 disabled:opacity-50">
                      {isSimulating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      {isSimulating ? 'Injecting...' : 'Inject to Queue'}
                    </button>
                  </div>
                </form>
              </div>
            </div>,
            document.body
          )}

          {/* MODAL: WhatsApp API Status */}
          {showWaModal && createPortal(
            <div className="modal-overlay" onClick={() => setShowWaModal(false)}>
              <div className="glass-modal max-w-xl w-full max-h-[90vh] overflow-y-auto my-auto p-6 rounded-2xl border border-white/10 space-y-5 animate-scale-in" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-white/08 pb-4">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-emerald-400" />
                    <div>
                      <h3 className="text-base font-bold text-white flex items-center gap-2">
                        WhatsApp Integration
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                          100% Free (₹0)
                        </span>
                      </h3>
                    </div>
                  </div>
                  <button onClick={() => setShowWaModal(false)} className="text-slate-500 hover:text-white p-1 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
                </div>

                {/* PRIMARY: WhatsApp Web Multi-Device */}
                <div className={`p-4 rounded-xl border ${baileysState?.status === 'CONNECTED' ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-white/05 border-white/10'} space-y-4`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-white text-sm">WhatsApp Web Device Linking</h4>
                      <p className="text-[11px] text-slate-400">Link your normal WhatsApp or WhatsApp Business mobile app in 5 seconds.</p>
                    </div>
                    {baileysState?.status === 'CONNECTED' ? (
                      <button
                        onClick={handleDisconnectBaileys}
                        disabled={loadingBaileys}
                        className="px-3 py-1.5 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/30 text-rose-300 font-semibold text-xs transition-colors cursor-pointer"
                      >
                        Unlink Device
                      </button>
                    ) : (
                      <button
                        onClick={handleStartBaileys}
                        disabled={loadingBaileys}
                        className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-lg shadow-emerald-500/20 text-xs"
                      >
                        <QrCode className="w-4 h-4" />
                        <span>{loadingBaileys ? 'Starting...' : (baileysState?.status === 'SCAN_QR' ? 'Refresh QR' : 'Link Phone / Show QR')}</span>
                      </button>
                    )}
                  </div>

                  {baileysState?.status === 'CONNECTED' ? (
                    <div className="pt-2 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                          <span className="text-white font-bold text-sm">
                            Connected: {baileysState.phone || diagnostics?.whatsapp?.displayPhoneNumber || 'Active Phone'}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          {baileysState.connectedAt ? `Linked on ${new Date(baileysState.connectedAt).toLocaleString()}` : 'Live & Active'} • Unlimited free messages & incoming orders!
                        </p>
                      </div>
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Active
                      </span>
                    </div>
                  ) : baileysState?.status === 'SCAN_QR' && baileysState.qrDataUrl ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center pt-2">
                      <div className="flex flex-col items-center p-3 bg-white rounded-xl shadow-inner">
                        <img src={baileysState.qrDataUrl} alt="WhatsApp QR Code" className="w-44 h-44 object-contain" />
                        <span className="text-slate-800 text-[11px] font-bold mt-1 animate-pulse">
                          Scan with WhatsApp Camera
                        </span>
                      </div>
                      <div className="space-y-2 text-xs">
                        <div className="font-bold text-white text-xs">Steps on your mobile:</div>
                        <ol className="list-decimal list-inside space-y-1 text-slate-300 text-[11px]">
                          <li>Open <b>WhatsApp</b> on phone</li>
                          <li>Tap <b>Menu (⋮)</b> or <b>Settings</b></li>
                          <li>Tap <b>Linked Devices</b></li>
                          <li>Tap <b>Link a Device</b> & scan QR</li>
                        </ol>
                        <button
                          onClick={() => handleStartBaileys(true)}
                          disabled={loadingBaileys}
                          className="btn-secondary !text-xs flex items-center gap-1.5 mt-2"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${loadingBaileys ? 'animate-spin' : ''}`} />
                          <span>Regenerate QR</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-400">
                      Device not linked. Click <b>"Link Phone / Show QR"</b> above to display the QR code and scan it with WhatsApp.
                    </div>
                  )}
                </div>

                {/* SECONDARY / COLLAPSIBLE: Meta Cloud API status */}
                <div className="p-3 rounded-xl bg-white/03 border border-white/06 space-y-2 text-xs">
                  <div className="flex items-center justify-between text-slate-400">
                    <span className="font-semibold text-slate-300">Meta Cloud API (Developer Option)</span>
                    <button onClick={handleTestWhatsApp} disabled={testingWa || !diagnostics?.whatsapp?.configured} className="btn-secondary flex items-center gap-1 !py-1 !text-[11px] disabled:opacity-50">
                      <RefreshCw className={`w-3 h-3 ${testingWa ? 'animate-spin' : ''}`} />
                      {testingWa ? 'Testing...' : 'Test Meta'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-400">
                    <div>Status: <span className="text-slate-200 font-semibold">{diagnostics?.whatsapp?.isMetaApiReachable ? 'Connected' : (diagnostics?.whatsapp?.lastError ? 'Error / Expired' : 'Not Configured')}</span></div>
                    <div>Phone: <span className="text-slate-200 font-mono">{diagnostics?.whatsapp?.displayPhoneNumber || '—'}</span></div>
                  </div>
                  {diagnostics?.whatsapp?.lastError && (
                    <div className="p-2 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-300 text-[10px]">
                      {diagnostics.whatsapp.lastError}
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <button onClick={() => setShowWaModal(false)} className="btn-secondary">Close</button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* MODAL: Advance Print Options */}
          {advanceModal.show && createPortal(
            <div className="modal-overlay" onClick={() => setAdvanceModal((p) => ({ ...p, show: false }))}>
              <div className="glass-modal max-w-lg w-full max-h-[90vh] overflow-y-auto my-auto p-6 rounded-2xl border border-cyan-500/25 space-y-5 animate-scale-in" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-white/08 pb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-cyan-500/12 border border-cyan-500/25 flex items-center justify-center text-cyan-400">
                      <Sliders className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-white">Advance Print Options</h3>
                      <p className="text-[11px] text-slate-500 truncate max-w-[260px]">{advanceModal.targetName}</p>
                    </div>
                  </div>
                  <button onClick={() => setAdvanceModal((p) => ({ ...p, show: false }))} className="text-slate-500 hover:text-white p-1 rounded-lg"><X className="w-4 h-4" /></button>
                </div>

                <div className="space-y-4 text-xs">
                  {/* Document Total Pages (Read-Only) */}
                  <div className="p-3 rounded-xl bg-slate-900/60 border border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Document Total Pages</div>
                        <div className="text-sm font-mono font-bold text-slate-100">
                          {advanceModal.options.pageCount || 1} {Number(advanceModal.options.pageCount) === 1 ? 'Page' : 'Pages'}
                        </div>
                      </div>
                    </div>
                    <span className="text-[11px] font-mono px-2.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 font-semibold">
                      Total in File
                    </span>
                  </div>

                  {/* Page Range to Print */}
                  <div>
                    <div className="flex justify-between items-center mb-1.5">
                      <label className="text-slate-300 font-semibold">Page Range to Print</label>
                      <span className="text-slate-500 font-mono">blank = all pages</span>
                    </div>
                    <input
                      type="text"
                      value={advanceModal.options.pageRange}
                      onChange={(e) => setAdvanceModal((p) => ({ ...p, options: { ...p.options, pageRange: e.target.value } }))}
                      onBlur={() => {
                        const totalP = Math.max(1, Number(advanceModal.options.pageCount) || 1);
                        setAdvanceModal((p) => ({
                          ...p,
                          options: {
                            ...p.options,
                            pageRange: clampPageRange(p.options.pageRange, totalP),
                          },
                        }));
                      }}
                      placeholder={`e.g. 1-${Math.min(3, Math.max(1, Number(advanceModal.options.pageCount) || 1))} (blank = all ${advanceModal.options.pageCount || 1} pages)`}
                      className="input-field font-mono"
                    />
                    {(() => {
                      const totalDocPages = Math.max(1, Number(advanceModal.options.pageCount) || 1);
                      const clampedRange = clampPageRange(advanceModal.options.pageRange, totalDocPages);
                      const selPages = countPagesInRange(clampedRange, totalDocPages);
                      const c = advanceModal.options.copies || 1;
                      const dup = advanceModal.options.duplex;
                      const col = advanceModal.options.colorMode === 'COLOR' && pricingConfig.colorPrintEnabled;
                      const estimated = calculateFilePrice(selPages, c, dup, col, pricingConfig);
                      const doublePages = dup ? Math.floor(selPages / 2) * 2 : 0;
                      const oddPages = dup ? selPages % 2 : selPages;

                      const presetButtons = [
                        { label: totalDocPages === 1 ? 'All Pages (1 pg)' : `All Pages (${totalDocPages} pgs)`, value: '' },
                        ...(totalDocPages >= 1 ? [{ label: 'Page 1 Only', value: '1' }] : []),
                        ...(totalDocPages === 2 ? [{ label: 'Page 2 Only', value: '2' }] : []),
                        ...(totalDocPages >= 3 ? [{ label: `Pages 1–${Math.min(3, totalDocPages)}`, value: `1-${Math.min(3, totalDocPages)}` }] : []),
                        ...(totalDocPages >= 5 ? [{ label: `Pages 1–${Math.min(5, totalDocPages)}`, value: `1-${Math.min(5, totalDocPages)}` }] : []),
                        ...(totalDocPages > 2 ? [{ label: `Last Page (${totalDocPages})`, value: `${totalDocPages}` }] : []),
                      ];

                      return (
                        <>
                          <div className="flex gap-1.5 mt-2 flex-wrap">
                            {presetButtons.map((btn) => (
                              <button
                                key={btn.label}
                                type="button"
                                onClick={() => setAdvanceModal((p) => ({ ...p, options: { ...p.options, pageRange: btn.value } }))}
                                className={`btn-secondary !py-1 !px-2.5 !text-[11px] font-mono ${(advanceModal.options.pageRange === btn.value || (!advanceModal.options.pageRange && !btn.value))
                                  ? '!bg-emerald-500/20 !border-emerald-500/40 !text-emerald-300 font-bold'
                                  : ''
                                  }`}
                              >
                                {btn.label}
                              </button>
                            ))}
                          </div>

                          <div className="mt-2.5 p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-between text-xs font-mono">
                            <span className="text-emerald-300">
                              Printing: <strong>{selPages} of {totalDocPages} page(s)</strong> × {c} copy{dup && oddPages > 0 ? ` (${doublePages} duplex + ${oddPages} single)` : ''}
                            </span>
                            <span className="text-emerald-400 font-bold bg-emerald-500/20 px-2.5 py-1 rounded text-sm shadow-sm">₹{estimated}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Duplex */}
                  <div>
                    <label className="block text-slate-300 font-semibold mb-1.5">Print Side</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[{ v: true, label: '2-Sided (Duplex)' }, { v: false, label: '1-Sided (Simplex)' }].map(({ v, label }) => (
                        <button key={String(v)} type="button" onClick={() => setAdvanceModal((p) => ({ ...p, options: { ...p.options, duplex: v } }))} className={`p-2.5 rounded-xl border text-center font-semibold flex items-center justify-center gap-2 transition-all ${advanceModal.options.duplex === v ? 'bg-emerald-500/15 border-emerald-500/35 text-emerald-300' : 'bg-black/20 border-white/08 text-slate-400 hover:text-slate-200'}`}>
                          <Layers className="w-3.5 h-3.5" /> {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Copies & Color */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-300 font-semibold mb-1.5">Copies</label>
                      <div className="flex items-center gap-1 bg-black/25 border border-white/08 rounded-xl p-1">
                        <button type="button" onClick={() => setAdvanceModal((p) => ({ ...p, options: { ...p.options, copies: Math.max(1, p.options.copies - 1) } }))} className="w-8 h-8 rounded-lg bg-white/06 hover:bg-white/10 text-white font-bold transition-all">-</button>
                        <input type="number" min="1" max="99" value={advanceModal.options.copies} onChange={(e) => setAdvanceModal((p) => ({ ...p, options: { ...p.options, copies: Math.max(1, parseInt(e.target.value, 10) || 1) } }))} className="flex-1 bg-transparent text-center font-bold font-mono text-white focus:outline-none" />
                        <button type="button" onClick={() => setAdvanceModal((p) => ({ ...p, options: { ...p.options, copies: Math.min(99, p.options.copies + 1) } }))} className="w-8 h-8 rounded-lg bg-white/06 hover:bg-white/10 text-white font-bold transition-all">+</button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-slate-300 font-semibold mb-1.5">Color Mode</label>
                      <div className="grid grid-cols-2 gap-1.5 h-[42px]">
                        {[{ v: 'BW', label: 'B&W' }, { v: 'COLOR', label: 'Colour' }].map(({ v, label }) => (
                          <button key={v} type="button" onClick={() => setAdvanceModal((p) => ({ ...p, options: { ...p.options, colorMode: v as ColorMode } }))} className={`rounded-xl border font-semibold transition-all ${advanceModal.options.colorMode === v ? 'bg-purple-500/15 border-purple-500/35 text-purple-300' : 'bg-black/20 border-white/08 text-slate-400'}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Orientation & Scale */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-300 font-semibold mb-1.5">Orientation</label>
                      <select value={advanceModal.options.orientation} onChange={(e) => setAdvanceModal((p) => ({ ...p, options: { ...p.options, orientation: e.target.value as Orientation } }))} className="input-field">
                        <option value="auto">Auto-Detect</option>
                        <option value="portrait">Portrait</option>
                        <option value="landscape">Landscape</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-300 font-semibold mb-1.5">Scale</label>
                      <button type="button" onClick={() => setAdvanceModal((p) => ({ ...p, options: { ...p.options, fitToPage: !p.options.fitToPage } }))} className={`w-full h-[40px] rounded-xl border font-semibold transition-all ${advanceModal.options.fitToPage ? 'bg-cyan-500/12 border-cyan-500/25 text-cyan-300' : 'bg-black/20 border-white/08 text-slate-400'}`}>
                        {advanceModal.options.fitToPage ? 'Fit to Page' : '100% Actual'}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/08">
                  <button type="button" onClick={() => setAdvanceModal((p) => ({ ...p, show: false }))} className="btn-secondary">Cancel</button>
                  <button type="button" onClick={handleSaveAdvanceModal} className="btn-primary flex items-center gap-2">
                    <Check className="w-4 h-4" /> Apply & Save
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          <Toast />
        </div>
      );
    }

    // ==========================================================================
    // TAB 2: WHATSAPP MESSAGES & TEMPLATES
    // ==========================================================================
    if (currentTab === 'messages') {
      const currentText = messages[activeMessageEdit] || '';

      const handleSaveMessages = async () => {
        setMessagesSaving(true); setMessagesSuccess(false);
        try {
          await api.updateMessages(messages, effectiveShopId);
          setMessagesSuccess(true);
          setTimeout(() => setMessagesSuccess(false), 3000);
        } catch (err: any) { showToast('Error', err.message, 'red'); }
        finally { setMessagesSaving(false); }
      };

      const insertPlaceholder = (ph: string) => setMessages({ ...messages, [activeMessageEdit]: currentText + ' ' + ph });

      const previewVars = {
        '{{shopName}}': shop?.name || 'Krish Test Shop',
        '{{customerName}}': 'John Doe',
        '{{fileCount}}': '5',
        '{{queueIndex}}': '1',
        '{{queueNumber}}': '42',
        '{{totalPages}}': '8',
        '{{totalPrice}}': '₹40',
        '{{phone}}': '+91 98765 43210',
        '{{customersAhead}}': '0',
        '{{customersAheadText}}': 'You are next in line (0 customers ahead of you).',
        '{{queuePosition}}': '1',
      };
      const preview = (tpl: string) => Object.entries(previewVars).reduce((s, [k, v]) => s.replace(k, v), tpl);

      const msgTabs = [
        { id: 'welcomeMessage', label: 'Welcome Reply', color: 'emerald' },
        { id: 'orderReceived', label: 'Order Received', color: 'emerald' },
        { id: 'printingConfirmation', label: 'Print Confirmation', color: 'cyan' },
        { id: 'expiryNotice', label: '23h Expiry Notice', color: 'amber' },
      ] as const;

      return (
        <div className="space-y-5 animate-fade-up">
          <TabBar />

          <div>
            <h1 className="text-xl font-bold text-white">WhatsApp Notifications & Message Templates</h1>
            <p className="text-sm text-slate-500 mt-1">Customize automated messages sent to customers after each event.</p>
          </div>

          {/* Notification Switches */}
          <div className="glass-card p-5 rounded-2xl border border-white/07 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-white flex items-center gap-2"><Send className="w-4 h-4 text-emerald-400" />WhatsApp Notification Controls</h2>
                <p className="text-[11px] text-slate-500 mt-0.5">Toggle master outbound messaging or customize individual automated notifications.</p>
              </div>
              {switchesSaving && <span className="text-xs text-slate-400 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin text-emerald-400" />Saving...</span>}
            </div>

            {/* Master Switch Banner */}
            {(() => {
              const isLockedByAdmin = shopSwitches.adminMessagingAllowed === false;
              const isShopkeeper = user?.role === 'shopkeeper';
              const isMasterActive = shopSwitches.allMessagesEnabled !== false && !isLockedByAdmin;

              return (
                <div className="space-y-3">
                  {isLockedByAdmin && (
                    <div className="p-3.5 rounded-xl border border-rose-500/40 bg-rose-500/15 flex items-center justify-between gap-3 text-rose-300">
                      <div className="flex items-center gap-2.5">
                        <span className="text-base">🔒</span>
                        <div>
                          <div className="font-bold text-xs uppercase tracking-wider text-rose-200">
                            Outbound Messaging Disabled by Platform Administrator
                          </div>
                          <div className="text-[11px] text-rose-300/80">
                            The administrator has restricted outbound WhatsApp messaging for this shop. You cannot send notifications or enable automated replies until permission is granted by your administrator.
                          </div>
                        </div>
                      </div>
                      <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-rose-500/20 text-rose-300 border border-rose-500/30 shrink-0">
                        ADMIN LOCKED
                      </span>
                    </div>
                  )}

                  <div className={`p-4 rounded-xl border transition-all ${isLockedByAdmin
                      ? 'bg-rose-950/20 border-rose-500/30'
                      : shopSwitches.allMessagesEnabled !== false
                        ? 'bg-emerald-500/10 border-emerald-500/30'
                        : 'bg-rose-500/10 border-rose-500/30'
                    }`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <span className={`w-2.5 h-2.5 rounded-full ${isLockedByAdmin
                              ? 'bg-rose-500'
                              : shopSwitches.allMessagesEnabled !== false
                                ? 'bg-emerald-400 animate-pulse'
                                : 'bg-rose-400'
                            }`} />
                          <span className="font-bold text-xs uppercase tracking-wider text-white">Master WhatsApp Messaging</span>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase ${isLockedByAdmin
                              ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                              : shopSwitches.allMessagesEnabled !== false
                                ? 'bg-emerald-500/20 text-emerald-300'
                                : 'bg-rose-500/20 text-rose-300'
                            }`}>
                            {isLockedByAdmin ? 'LOCKED BY ADMIN (OFF)' : (shopSwitches.allMessagesEnabled !== false ? 'ENABLED (ON)' : 'MUTED (OFF)')}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-300">
                          {isLockedByAdmin
                            ? 'Admin lock is active. Outbound WhatsApp messages will NOT be sent to customers under any circumstances.'
                            : (shopSwitches.allMessagesEnabled !== false
                              ? 'Outbound WhatsApp messages are active. You can customize which individual messages are sent below.'
                              : 'Silent / Pure Inbound Mode Active: Zero WhatsApp messages will be sent to customers (0% ban risk). Documents are received and printed completely silently.')}
                        </p>
                      </div>
                      <button
                        type="button"
                        disabled={isLockedByAdmin && isShopkeeper}
                        onClick={() => handleToggleSwitch('allMessagesEnabled')}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all shrink-0 shadow-md ${isLockedByAdmin && isShopkeeper
                            ? 'bg-slate-800 text-slate-500 border border-white/05 cursor-not-allowed opacity-60'
                            : shopSwitches.allMessagesEnabled !== false
                              ? 'bg-emerald-500 text-black hover:bg-emerald-400'
                              : 'bg-rose-500 text-white hover:bg-rose-400'
                          }`}
                      >
                        {isLockedByAdmin && isShopkeeper
                          ? '🔒 LOCKED BY ADMIN'
                          : (shopSwitches.allMessagesEnabled !== false ? 'MUTE ALL MESSAGES' : 'ENABLE ALL MESSAGES')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Sub-switches grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { key: 'welcomeMessageEnabled' as const, title: '1. Welcome Greeting', desc: 'Auto-reply instructions when customer sends hi, hello, or greeting.' },
                { key: 'orderReceivedEnabled' as const, title: '2. Order In Queue', desc: 'Immediate reply with queue number, file count, and cost estimate.' },
                { key: 'printingConfirmationEnabled' as const, title: '3. Print Complete', desc: 'Confirms printing completed, ready for collection at counter.' },
                { key: 'orderRevivedNoticeEnabled' as const, title: '4. Revived Order', desc: 'Alerts customer when their skipped or archived order is restored.' },
                { key: 'turnNextEnabled' as const, title: '5. Turn Next Alert', desc: "Tells customer they were skipped or called next in line." },
                { key: 'expiryNoticeEnabled' as const, title: '6. 23h Expiry Purge', desc: 'Notice sent before untouched files are purged for privacy.' },
              ].map(({ key, title, desc }) => {
                const isLockedByAdmin = shopSwitches.adminMessagingAllowed === false;
                const isShopkeeper = user?.role === 'shopkeeper';
                const isMasterOn = shopSwitches.allMessagesEnabled !== false && !isLockedByAdmin;
                const isOn = shopSwitches[key] !== false && !isLockedByAdmin;

                return (
                  <div key={key} className={`switch-card ${isOn ? 'enabled' : ''} ${!isMasterOn ? 'opacity-60' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-white text-xs">{title}</span>
                        {isLockedByAdmin ? (
                          <span className="text-[9px] font-mono px-1 py-0.2 rounded bg-rose-500/20 text-rose-300">
                            LOCKED
                          </span>
                        ) : !isMasterOn && (
                          <span className="text-[9px] font-mono px-1 py-0.2 rounded bg-rose-500/20 text-rose-300">
                            MUTED
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        disabled={isLockedByAdmin && isShopkeeper}
                        onClick={() => handleToggleSwitch(key)}
                        className={`toggle-switch ${isOn ? 'on' : 'off'} ${isLockedByAdmin && isShopkeeper ? 'cursor-not-allowed opacity-50' : ''}`}
                      />
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">{desc}</p>
                    <span className={`text-[10px] font-mono font-bold ${isOn ? 'text-emerald-400' : 'text-slate-600'}`}>
                      {isOn ? '● ENABLED' : '○ DISABLED'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Template Editor + Preview */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 glass-card p-6 rounded-2xl border border-white/07 space-y-4">
              {/* Template selector */}
              <div className="flex gap-1 p-1 rounded-xl bg-black/25 border border-white/08">
                {msgTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveMessageEdit(tab.id)}
                    className={`flex-1 py-2 px-2 rounded-lg text-xs font-semibold transition-all ${activeMessageEdit === tab.id ? (tab.color === 'emerald' ? 'bg-emerald-600 text-white' : tab.color === 'cyan' ? 'bg-cyan-600 text-white' : 'bg-amber-600 text-white') : 'text-slate-500 hover:text-slate-200'}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Placeholder chips */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Insert Variable:</label>
                <div className="flex flex-wrap gap-2">
                  {['{{shopName}}', '{{fileCount}}', '{{customersAheadText}}', '{{customersAhead}}', '{{queuePosition}}', '{{phone}}'].map((ph) => (
                    <button key={ph} type="button" onClick={() => insertPlaceholder(ph)} className="px-2.5 py-1 rounded-lg bg-black/20 hover:bg-black/30 border border-emerald-500/20 text-emerald-300 font-mono text-[11px] transition-all">
                      +{ph}
                    </button>
                  ))}
                </div>
              </div>

              {/* Textarea */}
              <div className="rounded-xl bg-black/25 border border-white/08 p-4 space-y-2">
                <textarea
                  rows={5} maxLength={1000} value={currentText}
                  onChange={(e) => setMessages({ ...messages, [activeMessageEdit]: e.target.value })}
                  placeholder="Type your WhatsApp notification template..."
                  className="w-full bg-transparent text-white text-sm focus:outline-none resize-none leading-relaxed font-mono"
                />
                <div className="flex justify-between items-center text-[11px] text-slate-500 pt-2 border-t border-white/06">
                  <span>Supports: *bold*, _italics_, ~strike~</span>
                  <span className="font-mono">{currentText.length} / 1000</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                {messagesSuccess && (
                  <span className="text-emerald-400 text-xs font-semibold flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4" /> Templates saved!
                  </span>
                )}
                <button onClick={handleSaveMessages} disabled={messagesSaving} className="btn-primary flex items-center gap-2 ml-auto disabled:opacity-50">
                  {messagesSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Save Templates
                </button>
              </div>
            </div>

            {/* WhatsApp Preview */}
            <div className="lg:col-span-5 glass-card p-6 rounded-2xl border border-white/07 space-y-4">
              <div>
                <h3 className="text-sm font-bold text-white flex items-center gap-2"><Smartphone className="w-4 h-4 text-emerald-400" />Live Preview</h3>
                <p className="text-[11px] text-slate-500 mt-1">How your customer sees the message on WhatsApp.</p>
              </div>

              <div className="bg-[#0b141a] rounded-2xl p-4 space-y-4 border border-slate-800">
                <div className="flex items-center gap-3 pb-3 border-b border-slate-800">
                  <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center font-bold text-white text-xs">PS</div>
                  <div>
                    <div className="text-xs font-semibold text-slate-200">{shop?.name || 'Print Shop Express'}</div>
                    <div className="text-[10px] text-emerald-400 font-mono">Verified Business</div>
                  </div>
                </div>

                {[
                  { tpl: messages.welcomeMessage, time: '7:51 PM' },
                  { tpl: messages.orderReceived, time: '7:52 PM' },
                  { tpl: messages.printingConfirmation, time: '7:55 PM' },
                  { tpl: messages.expiryNotice, time: '23h Inactive', amber: true },
                ].map(({ tpl, time, amber }, i) => (
                  <div key={i} className="flex flex-col items-end">
                    <div className={`wa-bubble max-w-[92%] text-xs ${amber ? '!bg-amber-950/80 !border !border-amber-600/30 text-amber-200' : ''}`} style={amber ? {} : {}}>
                      <p className="leading-relaxed whitespace-pre-wrap">{preview(tpl)}</p>
                      <div className={`text-[10px] text-right mt-1 font-mono ${amber ? 'text-amber-300' : 'text-emerald-200'}`}>{time} ✓✓</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-[11px] text-slate-500 bg-black/20 p-3 rounded-xl border border-white/07 leading-relaxed">
                💡 <strong className="text-slate-300">Zero-Cost Rule:</strong> Outbound messages are free within 24h of customer initiation. The 23h expiry guarantees cleanup before any paid window.
              </div>
            </div>
          </div>

          <Toast />
        </div>
      );
    }

    // ==========================================================================
    // TAB 3: ANALYTICS
    // ==========================================================================
    if (currentTab === 'analytics') {
      if (analyticsLoading || !analytics) {
        return (
          <div className="space-y-5 animate-fade-up">
            <TabBar />
            <div className="py-24 text-center text-slate-500 flex items-center justify-center gap-2">
              <RefreshCw className="w-5 h-5 animate-spin text-emerald-400" />
              <span>Loading analytics & paper inventory...</span>
            </div>
          </div>
        );
      }

      const { today, yesterday, week, month, year, allTime, chartSeries, recentEvents, inventory, series } = analytics;

      const filterSummaries: Record<string, any> = {
        today: today || {},
        yesterday: yesterday || {},
        week: week || {},
        month: month || {},
        year: year || {},
      };

      const selectedSummary = filterSummaries[chartFilter] || today || {};
      const rawSelectedSeries = (series && series[chartFilter]) || (chartFilter === 'week' ? chartSeries : []) || [];

      // If today or yesterday is selected and has no events yet, generate clean default time slots
      const selectedSeries = (rawSelectedSeries.length === 0 && (chartFilter === 'today' || chartFilter === 'yesterday'))
        ? ['08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00'].map((time) => ({
          label: time,
          sheets: 0,
          bw_sheets: 0,
          color_sheets: 0,
          revenue: 0,
          max_queue_count: 0,
          jobs: 0,
        }))
        : rawSelectedSeries;

      const maxSheets = Math.max(...((selectedSeries || []).map((s: any) => Number(s.sheets) || 0)), 10);
      const maxRevenue = Math.max(...((selectedSeries || []).map((s: any) => Number(s.revenue) || 0)), 50);

      const filterLabels: Record<string, string> = {
        today: 'Today',
        yesterday: 'Yesterday',
        week: 'This Week (7 Days)',
        month: 'This Month (30 Days)',
        year: 'This Year (12 Months)',
      };

      return (
        <div className="space-y-5 animate-fade-up">
          <TabBar />

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-emerald-400" />
                Print Analytics & Earnings
              </h1>
              <p className="text-sm text-slate-500 mt-1">Real-time tracking of physical sheets printed, revenue earned, and peak queue rush.</p>
            </div>

            {/* Range Selector Controls */}
            <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-black/40 border border-white/10 shadow-inner">
              {[
                { id: 'today', label: 'Today' },
                { id: 'yesterday', label: 'Yesterday' },
                { id: 'week', label: 'Week' },
                { id: 'month', label: 'Month' },
                { id: 'year', label: 'Year' },
              ].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setChartFilter(f.id as any)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${chartFilter === f.id
                      ? 'bg-emerald-500 text-black shadow-md shadow-emerald-500/20'
                      : 'text-slate-400 hover:text-white hover:bg-white/05'
                    }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Dynamic KPI Cards reflecting selected period */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                label: `${filterLabels[chartFilter]} Revenue`,
                value: `₹${Number(selectedSummary?.revenue || 0).toFixed(0)}`,
                sub: `${selectedSummary?.jobs || 0} completed job(s)`,
                accent: 'text-emerald-400',
                badge: 'EARNED',
              },
              {
                label: `${filterLabels[chartFilter]} Sheets Used`,
                value: `${selectedSummary?.sheets || 0} sheets`,
                sub: `${selectedSummary?.bw_sheets || 0} B&W · ${selectedSummary?.color_sheets || 0} Color`,
                accent: 'text-cyan-400',
                badge: 'PAPER',
              },
              {
                label: `${filterLabels[chartFilter]} Max in Queue`,
                value: `${selectedSummary?.max_queue_count || 0} customers`,
                sub: 'Peak customer rush recorded in queue',
                accent: 'text-amber-300',
                badge: 'PEAK RUSH',
              },
              {
                label: 'All-Time Lifetime',
                value: `₹${Number(allTime?.revenue || 0).toFixed(0)}`,
                sub: `${allTime?.sheets || 0} total lifetime sheets printed`,
                accent: 'text-purple-300',
                badge: 'LIFETIME',
              },
            ].map(({ label, value, sub, accent, badge }) => (
              <div key={label} className="stat-card p-5 rounded-2xl bg-white/5 border border-white/10 hover:border-white/20 transition-all shadow-md">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{label}</div>
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/08 text-slate-300 font-bold">{badge}</span>
                </div>
                <div className={`text-2xl md:text-3xl font-extrabold ${accent} font-mono tracking-tight`}>{value}</div>
                <div className="text-[11px] text-slate-400 mt-2 font-medium">{sub}</div>
              </div>
            ))}
          </div>

          {/* Multi-Range Bar Chart Card */}
          <div className="glass-card p-6 rounded-2xl border border-white/10 space-y-5 shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <Layers className="w-5 h-5 text-cyan-400" />
                  {filterLabels[chartFilter]} Print Usage & Earnings Bar Chart
                </h3>
                <p className="text-[12px] text-slate-400 mt-0.5">
                  {chartFilter === 'today' && 'Hourly sheets printed and revenue earned today in real-time'}
                  {chartFilter === 'yesterday' && 'Hourly sheets printed and revenue earned yesterday'}
                  {chartFilter === 'week' && 'Daily sheets printed, money earned, and peak queue rush over last 7 days'}
                  {chartFilter === 'month' && 'Daily sheets printed, money earned, and peak queue rush over last 30 days'}
                  {chartFilter === 'year' && 'Monthly sheets printed, revenue, and peak queue rush across the year'}
                </p>
              </div>

              {/* Metric View Controls */}
              <div className="flex items-center gap-1.5 p-1 rounded-xl bg-white/05 border border-white/10">
                <button
                  type="button"
                  onClick={() => setChartMetric('both')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${chartMetric === 'both' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : 'text-slate-400 hover:text-white'
                    }`}
                >
                  Dual (Sheets & ₹)
                </button>
                <button
                  type="button"
                  onClick={() => setChartMetric('sheets')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${chartMetric === 'sheets' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : 'text-slate-400 hover:text-white'
                    }`}
                >
                  Sheets Only
                </button>
                <button
                  type="button"
                  onClick={() => setChartMetric('revenue')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${chartMetric === 'revenue' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'text-slate-400 hover:text-white'
                    }`}
                >
                  Revenue Only
                </button>
              </div>
            </div>

            {/* Bar Chart Container */}
            {selectedSeries && selectedSeries.length > 0 ? (
              <div className="space-y-3">
                {/* Visual Chart Legend */}
                <div className="flex items-center gap-4 text-xs font-mono text-slate-400 pt-1 border-b border-white/05 pb-3">
                  {(chartMetric === 'both' || chartMetric === 'sheets') && (
                    <div className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded bg-cyan-400 shadow-sm shadow-cyan-500/40" />
                      <span className="text-cyan-300 font-bold">Sheets Used</span>
                    </div>
                  )}
                  {(chartMetric === 'both' || chartMetric === 'revenue') && (
                    <div className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded bg-emerald-400 shadow-sm shadow-emerald-500/40" />
                      <span className="text-emerald-300 font-bold">Revenue (₹)</span>
                    </div>
                  )}
                  {(chartFilter === 'week' || chartFilter === 'month' || chartFilter === 'year') && (
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                      <span className="text-amber-300 font-medium">Max in Queue (Peak Rush)</span>
                    </div>
                  )}
                </div>

                {/* Bars Grid */}
                <div className="h-64 flex items-end gap-2 md:gap-3 pt-6 overflow-x-auto pb-2">
                  {selectedSeries.map((d: any, idx: number) => {
                    const sheets = Number(d.sheets) || 0;
                    const revenue = Number(d.revenue) || 0;
                    const peakQueue = Number(d.max_queue_count) || 0;

                    const sheetHeightPct = Math.max(sheets > 0 ? 8 : 2, Math.round((sheets / maxSheets) * 100));
                    const revenueHeightPct = Math.max(revenue > 0 ? 8 : 2, Math.round((revenue / maxRevenue) * 100));

                    return (
                      <div key={d.label || idx} className="flex-1 min-w-[38px] md:min-w-[48px] flex flex-col items-center gap-2 group">
                        {/* Hover Tooltip Value */}
                        <div className="text-[10px] font-mono text-slate-300 text-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/90 px-1.5 py-0.5 rounded border border-white/10 shrink-0 whitespace-nowrap shadow-md pointer-events-none z-10">
                          {sheets} sh · ₹{revenue}
                          {peakQueue > 0 && <span className="block text-amber-300 font-bold">Max Queue: {peakQueue}</span>}
                        </div>

                        {/* Bars Area */}
                        <div className="relative flex items-end justify-center gap-1 flex-1 w-full">
                          {/* Sheets Bar */}
                          {(chartMetric === 'both' || chartMetric === 'sheets') && (
                            <div
                              style={{ height: `${sheetHeightPct}%` }}
                              className={`rounded-t-lg transition-all cursor-pointer shadow-md ${chartMetric === 'both' ? 'w-1/2' : 'w-full'
                                } ${sheets > 0
                                  ? 'bg-gradient-to-t from-cyan-600 to-cyan-300 hover:from-cyan-500 hover:to-cyan-200 shadow-cyan-500/20'
                                  : 'bg-white/05 hover:bg-white/10'
                                }`}
                              title={`${d.label}: ${sheets} sheets printed (${d.bw_sheets || 0} B&W, ${d.color_sheets || 0} Color)`}
                            />
                          )}

                          {/* Revenue Bar */}
                          {(chartMetric === 'both' || chartMetric === 'revenue') && (
                            <div
                              style={{ height: `${revenueHeightPct}%` }}
                              className={`rounded-t-lg transition-all cursor-pointer shadow-md ${chartMetric === 'both' ? 'w-1/2' : 'w-full'
                                } ${revenue > 0
                                  ? 'bg-gradient-to-t from-emerald-600 to-emerald-300 hover:from-emerald-500 hover:to-emerald-200 shadow-emerald-500/20'
                                  : 'bg-white/05 hover:bg-white/10'
                                }`}
                              title={`${d.label}: ₹${revenue} earned`}
                            />
                          )}
                        </div>

                        {/* Peak Queue Badge on day if > 0 */}
                        {peakQueue > 0 && (
                          <span className="text-[9px] font-mono font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1 rounded" title={`Peak Queue on ${d.label}: ${peakQueue} customers`}>
                            {peakQueue}Q
                          </span>
                        )}

                        {/* Values label */}
                        <div className="text-[10px] font-mono font-bold text-slate-300 text-center leading-tight">
                          {chartMetric === 'revenue' ? `₹${revenue}` : `${sheets}s`}
                        </div>

                        {/* X-Axis Label */}
                        <span className="text-[10px] text-slate-400 font-mono text-center truncate max-w-full" title={d.label}>
                          {d.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-500 text-xs">
                No print data found for this range yet. Completed print jobs will automatically plot here!
              </div>
            )}
          </div>

          {/* Storage Optimization Architecture Banner */}
          <div className="p-4 rounded-2xl bg-emerald-500/08 border border-emerald-500/20 flex items-start gap-3 shadow-sm">
            <span className="text-xl">🛡️</span>
            <div className="space-y-0.5">
              <h4 className="text-xs font-bold text-emerald-300 uppercase tracking-wider flex items-center gap-2">
                Server Storage Protected & Lifetime Analytics Preserved
              </h4>
              <p className="text-[11px] text-slate-300 leading-relaxed">
                Individual per-print log events are kept for 2 days to maintain 100% free server storage. Permanent daily summaries (<strong className="text-white">sheets used</strong>, <strong className="text-white">revenue earned</strong>, and <strong className="text-amber-300">maximum in-queue rush</strong>) are permanently stored in ultra-compact daily statistics (&lt;50 bytes/day), so your server storage will never run out of space!
              </p>
            </div>
          </div>

          {/* Recent Events Table */}
          <div className="glass-card p-6 rounded-2xl border border-white/07 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-white">Recent Print Events (Last 48 Hours)</h3>
              <span className="text-[11px] text-slate-500 font-mono">Kept for 2 days for storage optimization</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="border-b border-white/07 text-slate-500 uppercase text-[11px]">
                  <tr>
                    {['Timestamp', 'Pages', 'Sheets', 'Mode', 'Revenue'].map((h, i) => (
                      <th key={h} className={`py-2.5 px-3 font-semibold tracking-wider ${i === 4 ? 'text-right' : ''}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/04 font-mono">
                  {recentEvents && recentEvents.length > 0 ? recentEvents.map((evt: any) => (
                    <tr key={evt.id} className="hover:bg-white/03 text-slate-300">
                      <td className="py-2.5 px-3 text-slate-500">{new Date(evt.at).toLocaleString()}</td>
                      <td className="py-2.5 px-3 font-bold text-white">{evt.pages}</td>
                      <td className="py-2.5 px-3">{evt.sheets}</td>
                      <td className="py-2.5 px-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${evt.color_mode === 'COLOR' ? 'bg-purple-500/12 text-purple-400' : 'bg-white/06 text-slate-400'}`}>{evt.color_mode}</span>
                      </td>
                      <td className="py-2.5 px-3 text-right font-bold text-emerald-400">₹{evt.revenue}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={5} className="py-8 text-center text-slate-600">No print events in the last 2 days.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Toast />
        </div>
      );
    }

    // ==========================================================================
    // TAB 4: SETTINGS (Printer Management, Pricing, System)
    // ==========================================================================
    if (currentTab === 'settings') {
      const enabledPrinters = printers.filter((p) => p.enabled);

      const pendingDevicesCount = shopDevices.filter((d) => !d.approved).length;

      const settingsSections = [
        {
          id: 'whatsapp',
          label: 'WhatsApp Linking',
          icon: <Smartphone className="w-4 h-4 text-emerald-400" />,
          badge: baileysState?.status === 'CONNECTED' ? 'Linked' : 'Scan QR',
          badgeColor: undefined,
        },
        { id: 'printers', label: 'Printer Management', icon: <Printer className="w-4 h-4" /> },
        { id: 'pricing', label: 'Pricing & Color Rates', icon: <IndianRupee className="w-4 h-4" /> },
        {
          id: 'devices',
          label: 'Browser Access & Devices',
          icon: <ShieldCheck className="w-4 h-4 text-emerald-400" />,
          badge: pendingDevicesCount > 0 ? `${pendingDevicesCount} Pending` : undefined,
          badgeColor: pendingDevicesCount > 0 ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30 animate-pulse' : undefined,
        },
        { id: 'subscribers', label: 'Customer Subscriptions', icon: <Sparkles className="w-4 h-4 text-amber-400" />, badge: 'VIP Pass' },
        { id: 'agent', label: 'Download Shop Agent', icon: <Laptop className="w-4 h-4 text-emerald-400" />, badge: '1-Click Run' },
        { id: 'system', label: 'System Config', icon: <Monitor className="w-4 h-4" /> },
      ] as const;

      return (
        <div className="space-y-5 animate-fade-up">
          <TabBar />

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-white">Settings & Configuration</h1>
              <p className="text-sm text-slate-500 mt-1">Manage active printers, print pricing, agent download, and system preferences.</p>
            </div>
            <button
              type="button"
              onClick={handleDownloadAgent}
              disabled={downloadingAgent}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-brand-600 hover:from-emerald-500 hover:to-brand-500 text-white font-bold text-xs shadow-lg shadow-emerald-600/25 flex items-center gap-2 transition-all self-start sm:self-auto cursor-pointer disabled:opacity-50"
              title="Download 1-Click Shop Agent package for this PC"
            >
              {downloadingAgent ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span>{downloadingAgent ? 'Generating Package...' : 'Download Agent (.zip)'}</span>
            </button>
          </div>

          {/* Quick Offline Alert with Direct Download */}
          {!diagnostics?.agent?.online && (
            <div className="p-4 rounded-2xl bg-gradient-to-r from-emerald-950/60 via-slate-900 to-emerald-950/40 border border-emerald-500/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-lg shadow-black/40">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0">
                  <Laptop className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-white">Agent is Offline on this PC</div>
                  <div className="text-xs text-slate-400">Download the pre-configured zip, extract it, and double-click <code>START_AGENT.bat</code>.</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setSettingsSection('agent')}
                  className="px-3 py-1.5 rounded-lg bg-white/05 hover:bg-white/10 text-xs font-semibold text-slate-200 border border-white/10 transition-all"
                >
                  View Instructions
                </button>
                <button
                  type="button"
                  onClick={handleDownloadAgent}
                  disabled={downloadingAgent}
                  className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-xs font-bold text-white flex items-center gap-1.5 shadow-md shadow-emerald-600/30 transition-all cursor-pointer disabled:opacity-50"
                >
                  {downloadingAgent ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  <span>{downloadingAgent ? 'Preparing...' : 'Download .zip'}</span>
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Sidebar */}
            <div className="lg:col-span-3">
              <div className="glass-card rounded-2xl p-3 border border-white/07 space-y-1">
                {settingsSections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSettingsSection(s.id)}
                    className={`settings-nav-item w-full flex items-center justify-between ${settingsSection === s.id ? 'active' : ''}`}
                  >
                    <div className="flex items-center gap-2.5">
                      {s.icon}
                      <span>{s.label}</span>
                    </div>
                    {'badge' in s && s.badge && (
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${'badgeColor' in s && s.badgeColor
                          ? s.badgeColor
                          : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        }`}>
                        {s.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-9">

              {/* ── WHATSAPP DEVICE LINKING (OPTION 2: 100% FREE FOREVER) ── */}
              {settingsSection === 'whatsapp' && (
                <div className="glass-card rounded-2xl p-6 border border-white/07 space-y-6 animate-fade-in">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-white/07 pb-4 gap-3">
                    <div>
                      <h2 className="text-base font-bold text-white flex items-center gap-2">
                        <Smartphone className="w-5 h-5 text-emerald-400" />
                        WhatsApp Device Linking
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                          100% Free Forever (₹0)
                        </span>
                      </h2>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Link your phone to PrintBridge. Customers send PDFs or photos directly to your shop's WhatsApp number.
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      {baileysState?.status === 'CONNECTED' ? (
                        <button
                          type="button"
                          onClick={handleDisconnectBaileys}
                          disabled={loadingBaileys}
                          className="px-3.5 py-1.5 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/30 text-rose-300 text-xs font-semibold transition-colors cursor-pointer"
                        >
                          Unlink Phone
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleStartBaileys(baileysState?.status === 'SCAN_QR')}
                          disabled={loadingBaileys}
                          className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-lg shadow-emerald-500/20 text-xs"
                        >
                          <QrCode className="w-4 h-4" />
                          <span>{loadingBaileys ? 'Starting...' : (baileysState?.status === 'SCAN_QR' ? 'Refresh QR Code' : 'Scan QR to Link Phone')}</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Security & Safety Notice */}
                  <div className="p-4 rounded-xl bg-emerald-500/08 border border-emerald-500/20 space-y-2 text-xs">
                    <div className="flex items-center gap-2 font-bold text-emerald-300">
                      <ShieldCheck className="w-4 h-4 text-emerald-400" />
                      100% Safe, Private & Self-Hosted
                    </div>
                    <p className="text-slate-300 text-[11px] leading-relaxed">
                      PrintBridge uses the official <b>WhatsApp Multi-Device WebSocket protocol</b> (identical to WhatsApp Web on your PC).
                      All chat keys are stored securely inside your private Oracle Cloud instance and never shared with third parties.
                      You retain full control and can unlink your device at any moment.
                    </p>
                  </div>

                  {/* Connection Card */}
                  {baileysState?.status === 'CONNECTED' ? (
                    <div className="p-5 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
                            <span className="text-white font-bold text-base">
                              Connected: {baileysState.phone || diagnostics?.whatsapp?.displayPhoneNumber || 'Active WhatsApp'}
                            </span>
                          </div>
                          <p className="text-slate-400 text-xs">
                            {baileysState.connectedAt ? `Linked on ${new Date(baileysState.connectedAt).toLocaleString()}` : 'Live & Active Multi-Device Session'}
                          </p>
                        </div>
                        <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1.5 self-start sm:self-auto">
                          <CheckCircle2 className="w-4 h-4" /> Ready for Live Orders
                        </span>
                      </div>

                      <div className="p-3.5 rounded-xl bg-slate-900/60 border border-white/08 text-xs text-slate-300 space-y-1.5">
                        <div className="font-semibold text-white flex items-center gap-1.5">
                          <span>✨ How it works for your customers:</span>
                        </div>
                        <ul className="list-disc list-inside space-y-1 text-slate-400 text-[11px]">
                          <li>Customers message their PDF, Word, or photo documents to <b>{baileysState.phone || 'your shop WhatsApp number'}</b>.</li>
                          <li>PrintBridge instantly adds the file to your <b>Live Print Queue</b>.</li>
                          <li>Your shop desktop agent spools the document directly to your printer!</li>
                          <li>Automated collection notices are sent back to the customer — completely free of cost.</li>
                        </ul>
                      </div>
                    </div>
                  ) : baileysState?.status === 'SCAN_QR' && baileysState.qrDataUrl ? (
                    <div className="p-5 rounded-2xl bg-white/04 border border-white/10 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                        <div className="flex flex-col items-center p-4 bg-white rounded-2xl shadow-xl">
                          <img src={baileysState.qrDataUrl} alt="WhatsApp QR Code" className="w-56 h-56 object-contain" />
                          <span className="text-slate-900 text-xs font-bold mt-2 animate-pulse flex items-center gap-1.5">
                            <QrCode className="w-4 h-4 text-emerald-600" /> Point WhatsApp Camera Here
                          </span>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <h3 className="font-bold text-white text-sm">4 Quick Steps to Link:</h3>
                            <p className="text-slate-400 text-[11px] mt-0.5">Use your shop's phone to scan this QR code.</p>
                          </div>

                          <ol className="space-y-2.5 text-slate-300 text-xs list-decimal list-inside">
                            <li>Open <b>WhatsApp</b> on your mobile phone.</li>
                            <li>Tap <b>Menu (⋮)</b> on Android or <b>Settings</b> on iPhone.</li>
                            <li>Select <b>Linked Devices</b>.</li>
                            <li>Tap <b>Link a Device</b> and point camera at the code.</li>
                          </ol>

                          <div className="pt-2 flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => handleStartBaileys(true)}
                              disabled={loadingBaileys}
                              className="btn-secondary !text-xs flex items-center gap-1.5"
                            >
                              <RefreshCw className={`w-3.5 h-3.5 ${loadingBaileys ? 'animate-spin' : ''}`} />
                              <span>Regenerate QR</span>
                            </button>
                            <span className="text-emerald-400 text-[11px] font-medium flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                              Listening for scan...
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-8 rounded-2xl bg-white/03 border border-white/07 text-center space-y-4">
                      <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 mx-auto flex items-center justify-center">
                        <QrCode className="w-8 h-8" />
                      </div>
                      <div className="max-w-md mx-auto space-y-1">
                        <h3 className="text-sm font-bold text-white">No WhatsApp Account Linked</h3>
                        <p className="text-xs text-slate-400">
                          Click below to generate a secure QR code. Once scanned, your shop will receive live print orders directly from WhatsApp!
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleStartBaileys}
                        disabled={loadingBaileys}
                        className="px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold inline-flex items-center gap-2 shadow-lg shadow-emerald-500/20 text-xs transition-all cursor-pointer"
                      >
                        <QrCode className="w-4 h-4" />
                        <span>{loadingBaileys ? 'Generating QR...' : 'Generate WhatsApp QR Code'}</span>
                      </button>
                    </div>
                  )}

                  {/* Secondary Collapsible: Meta Developer API Fallback */}
                  <div className="p-4 rounded-xl bg-white/03 border border-white/06 space-y-2 text-xs">
                    <div className="flex items-center justify-between text-slate-400">
                      <div>
                        <span className="font-semibold text-slate-300">Legacy Meta Developer Cloud API</span>
                        <span className="text-[11px] text-slate-500 block">Optional fallback if using Facebook Business Manager tokens.</span>
                      </div>
                      <button
                        type="button"
                        onClick={handleTestWhatsApp}
                        disabled={testingWa || !diagnostics?.whatsapp?.configured}
                        className="btn-secondary flex items-center gap-1 !py-1 !text-[11px] disabled:opacity-50"
                      >
                        <RefreshCw className={`w-3 h-3 ${testingWa ? 'animate-spin' : ''}`} />
                        {testingWa ? 'Testing...' : 'Test Meta Connection'}
                      </button>
                    </div>
                    {diagnostics?.whatsapp?.lastError && (
                      <div className="p-2 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-300 text-[10px]">
                        {diagnostics.whatsapp.lastError}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── PRINTER MANAGEMENT ── */}
              {settingsSection === 'printers' && (
                <div className="glass-card rounded-2xl p-6 border border-white/07 space-y-6 animate-fade-in">
                  <div className="flex items-center justify-between border-b border-white/07 pb-4">
                    <div>
                      <h2 className="text-base font-bold text-white flex items-center gap-2">
                        <Printer className="w-5 h-5 text-emerald-400" />
                        Printer Management
                      </h2>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Add, remove, and turn printers ON or OFF. Each time you print, pick any active printer.
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setShowAddPrinterModal(true)}
                        className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 font-bold shadow-lg shadow-emerald-500/10"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add New Printer</span>
                      </button>
                      <div className="text-xs text-slate-400 font-mono bg-white/04 px-2.5 py-1 rounded-lg border border-white/07">
                        <span className="text-emerald-400 font-bold">{enabledPrinters.length}</span> of {printers.length} turned ON
                      </div>
                    </div>
                  </div>

                  {/* Printer List */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Available Printers</div>
                      {printers.length > 0 && (
                        <button
                          onClick={handleResetDefaultPrinters}
                          className="text-[10px] text-slate-500 hover:text-slate-300 font-mono underline decoration-dotted"
                          title="Restore original default printer list"
                        >
                          Reset Defaults
                        </button>
                      )}
                    </div>

                    {printers.length === 0 ? (
                      <div className="p-8 text-center rounded-xl bg-white/02 border border-dashed border-white/10 space-y-3">
                        <Printer className="w-8 h-8 text-slate-600 mx-auto" />
                        <p className="text-sm text-slate-400 font-medium">No printers configured yet</p>
                        <p className="text-xs text-slate-600 max-w-sm mx-auto">
                          Add the exact name of any physical printer connected to your PC to start printing to it.
                        </p>
                        <button
                          onClick={() => setShowAddPrinterModal(true)}
                          className="btn-primary text-xs py-2 px-4 inline-flex items-center gap-1.5 font-bold"
                        >
                          <Plus className="w-4 h-4" /> Add Your First Printer
                        </button>
                      </div>
                    ) : (
                      printers.map((printer) => (
                        <div
                          key={printer.id}
                          className={`printer-row ${!printer.enabled ? 'opacity-60' : ''}`}
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1 mr-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${printer.enabled ? 'bg-emerald-500/15 border border-emerald-500/25 text-emerald-400' : 'bg-white/04 border border-white/06 text-slate-600'}`}>
                              <Printer className="w-5 h-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`font-semibold text-sm truncate max-w-[280px] ${printer.enabled ? 'text-white' : 'text-slate-500'}`} title={printer.name}>{printer.name}</span>
                                {printer.enabled ? (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 font-mono font-bold flex-shrink-0">
                                    ● READY (ON)
                                  </span>
                                ) : (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-400 font-mono flex-shrink-0">
                                    ○ OFF
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleTogglePrinterDuplex(printer.id)}
                                  className={`text-[10px] px-2.5 py-0.5 rounded-full font-mono font-bold flex-shrink-0 cursor-pointer border transition-all ${printer.duplexSupported !== false
                                    ? 'bg-purple-500/20 text-purple-300 border-purple-500/35 hover:bg-purple-500/30'
                                    : 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30 hover:bg-cyan-500/25'
                                    }`}
                                  title="Click to toggle Auto Duplex (Hardware 2-Sided) capability"
                                >
                                  {printer.duplexSupported !== false ? '🔄 Auto-Duplex (2-Sided)' : '📄 1-Sided (Simplex Only)'}
                                </button>
                              </div>
                              <div className="flex items-center gap-2 text-[11px] text-slate-500 font-mono mt-0.5 flex-wrap">
                                <span>{printer.type}</span>
                                <span className="text-slate-400">
                                  • Windows System Name: <strong className="text-emerald-400/90 font-mono">{printer.systemName || printer.name}</strong>
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-3 flex-shrink-0">
                            {/* Enable/Disable toggle */}
                            <div className="flex items-center gap-2">
                              <span className={`text-[11px] font-mono font-semibold ${printer.enabled ? 'text-emerald-400' : 'text-slate-600'}`}>
                                {printer.enabled ? 'ON' : 'OFF'}
                              </span>
                              <button
                                onClick={() => togglePrinter(printer.id)}
                                title={printer.enabled ? 'Turn OFF printer' : 'Turn ON printer'}
                                className={`toggle-switch ${printer.enabled ? 'on' : 'off'}`}
                              />
                            </div>

                            {/* Delete Printer Button */}
                            <button
                              onClick={() => handleDeletePrinter(printer.id, printer.name)}
                              title={`Delete ${printer.name}`}
                              className="p-1.5 rounded-lg text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors ml-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Printer Info */}
                  <div className="p-4 rounded-xl bg-white/04 border border-white/07 text-xs text-slate-500 space-y-2">
                    <div className="font-semibold text-slate-300 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 text-emerald-400" />
                      How Real Printers Work With PrintBridge
                    </div>
                    <ul className="space-y-1.5 text-[11px] leading-relaxed">
                      <li>• <strong className="text-slate-300">Exact Windows Name:</strong> When adding a printer, enter its exact name as shown in Windows <em>Settings &gt; Bluetooth &amp; Devices &gt; Printers &amp; Scanners</em>.</li>
                      <li>• <strong className="text-slate-300">Direct Hardware Spooling:</strong> When you click Print on an order, PrintBridge sends the document directly to that Windows printer driver — printing on real physical paper!</li>
                      <li>• <strong className="text-slate-300">Add or Remove Anytime:</strong> Add as many printers as you have in the shop (e.g. Laser for B&amp;W, Inkjet for Color, Receipt printer, etc.) and delete any printer you no longer use.</li>
                      <li>• <strong className="text-slate-300">Turn printers ON or OFF</strong> to control which printers appear in the one-click print selection modal.</li>
                    </ul>
                  </div>

                  {/* Add Printer Modal */}
                  {showAddPrinterModal && createPortal(
                    <div className="modal-overlay" onClick={() => setShowAddPrinterModal(false)}>
                      <div className="glass-modal max-w-xl w-full max-h-[90vh] overflow-y-auto my-auto p-6 rounded-2xl border border-white/10 space-y-4 animate-scale-in" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between pb-3 border-b border-white/08">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 flex items-center justify-center">
                              <Printer className="w-4 h-4" />
                            </div>
                            <div>
                              <h3 className="text-sm font-bold text-white">Add New Printer</h3>
                              <p className="text-[11px] text-slate-500">Connect any physical printer installed on this PC</p>
                            </div>
                          </div>
                          <button
                            onClick={() => setShowAddPrinterModal(false)}
                            className="p-1 rounded-lg text-slate-500 hover:text-white hover:bg-white/06"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        <form onSubmit={handleAddPrinter} className="space-y-4 pt-3">
                          <div className="p-3 rounded-xl bg-emerald-500/08 border border-emerald-500/20 text-xs text-emerald-300 space-y-1">
                            <div className="font-semibold flex items-center gap-1.5">
                              <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                              How Display Name &amp; Real Windows Name work:
                            </div>
                            <p className="text-[11px] text-emerald-200/80 leading-relaxed">
                              The <strong>Display Name</strong> is what you and your staff see on the website. The <strong>Real Windows Name</strong> is what the print agent uses to send the print job to your actual printer hardware.
                            </p>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-slate-300 mb-1">
                              Friendly Display Name <span className="text-rose-400">*</span>
                            </label>
                            <input
                              type="text"
                              required
                              autoFocus
                              placeholder="e.g. Front Counter Fast Laser or Shop Color Xerox"
                              value={newPrinterDisplayName}
                              onChange={(e) => setNewPrinterDisplayName(e.target.value)}
                              className="input-field text-sm text-white w-full"
                            />
                            <p className="text-[10px] text-slate-500 mt-1">
                              This friendly name appears on this website and in the print selector modal.
                            </p>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-slate-300 mb-1">
                              Real Windows Printer Name <span className="text-emerald-400 font-mono text-[10px] font-normal">(System Driver)</span>
                            </label>
                            <input
                              type="text"
                              placeholder="e.g. Pantum-M6500NW-Series 5804F8 or Canon LBP2900"
                              value={newPrinterSystemName}
                              onChange={(e) => setNewPrinterSystemName(e.target.value)}
                              className="input-field text-sm font-mono text-emerald-300 w-full"
                            />
                            <p className="text-[10px] text-slate-500 mt-1">
                              Exact name in Windows <em>Settings &gt; Bluetooth &amp; Devices &gt; Printers &amp; Scanners</em>. If blank, display name is used.
                            </p>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-slate-300 mb-1">
                              Printer Type / Description
                            </label>
                            <select
                              value={newPrinterType}
                              onChange={(e) => setNewPrinterType(e.target.value)}
                              className="input-field text-xs text-white w-full"
                            >
                              <option value="USB Laser">USB Laser (Black &amp; White)</option>
                              <option value="Network Laser">Network / Wi-Fi Laser</option>
                              <option value="Color Inkjet">Color Inkjet / Tank</option>
                              <option value="Duplex Heavy-Duty">Duplex Heavy-Duty Xerox / Copier</option>
                              <option value="Thermal Receipt">Thermal / Receipt (80mm/58mm)</option>
                              <option value="Virtual PDF">Virtual PDF</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-slate-300 mb-1">
                              Hardware Duplex Capability (2-Sided Printing)
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                type="button"
                                onClick={() => setNewPrinterDuplex(true)}
                                className={`p-2 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${newPrinterDuplex
                                  ? 'bg-purple-500/20 border-purple-500/40 text-purple-300 shadow-sm'
                                  : 'bg-black/20 border-white/08 text-slate-400'
                                  }`}
                              >
                                <Layers className="w-3.5 h-3.5" /> Auto-Duplex (2-Sided)
                              </button>
                              <button
                                type="button"
                                onClick={() => setNewPrinterDuplex(false)}
                                className={`p-2 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${!newPrinterDuplex
                                  ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300 shadow-sm'
                                  : 'bg-black/20 border-white/08 text-slate-400'
                                  }`}
                              >
                                <FileText className="w-3.5 h-3.5" /> 1-Sided (Simplex Only)
                              </button>
                            </div>
                            <p className="text-[10px] text-slate-500 mt-1">
                              Auto-Duplex printers flip pages automatically. 1-Sided printers avoid paper flip delays.
                            </p>
                          </div>

                          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/08">
                            <button
                              type="button"
                              onClick={() => setShowAddPrinterModal(false)}
                              className="btn-secondary text-xs py-2 px-3"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5 font-bold"
                            >
                              <Plus className="w-3.5 h-3.5" />
                              <span>Save &amp; Add Printer</span>
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
              )}

              {/* ── PRICING & COLOR RATES ── */}
              {settingsSection === 'pricing' && (
                <div className="glass-card rounded-2xl p-6 border border-white/07 space-y-6 animate-fade-in">
                  <div className="flex items-center justify-between border-b border-white/07 pb-4">
                    <div>
                      <h2 className="text-base font-bold text-white flex items-center gap-2">
                        <IndianRupee className="w-5 h-5 text-emerald-400" />
                        Print Pricing & Color Configuration
                      </h2>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Set pricing per page for single/double side prints and toggle Color Printing ON or OFF.
                      </p>
                    </div>
                    {pricingSaving && (
                      <span className="text-xs text-slate-400 flex items-center gap-1.5 font-mono">
                        <RefreshCw className="w-3.5 h-3.5 animate-spin text-emerald-400" />
                        Saving rates...
                      </span>
                    )}
                  </div>

                  {/* Color Print Master Toggle Switch */}
                  <div className={`p-5 rounded-2xl border transition-all ${pricingConfig.colorPrintEnabled
                    ? 'bg-purple-950/20 border-purple-500/30 shadow-lg shadow-purple-950/20'
                    : 'bg-white/03 border-white/08'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${pricingConfig.colorPrintEnabled ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' : 'bg-white/05 text-slate-500 border border-white/08'
                          }`}>
                          <Palette className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-white flex items-center gap-2">
                            <span>Color Printing Availability</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono font-bold ${pricingConfig.colorPrintEnabled ? 'bg-purple-500/20 text-purple-300' : 'bg-white/06 text-slate-500'
                              }`}>
                              {pricingConfig.colorPrintEnabled ? '● ENABLED' : '○ DISABLED'}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">
                            When turned OFF, the shop operates in B&W-only mode. Color buttons across all orders will be disabled.
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPricingConfig((p) => ({ ...p, colorPrintEnabled: !p.colorPrintEnabled }))}
                        className={`toggle-switch ${pricingConfig.colorPrintEnabled ? 'on' : 'off'}`}
                        title={pricingConfig.colorPrintEnabled ? 'Click to disable color printing' : 'Click to enable color printing'}
                      />
                    </div>
                  </div>

                  {/* Rates Configuration Cards */}
                  <div className="space-y-4">
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Per-Page Auto-Calculation Rates (INR ₹)
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* B&W Rates */}
                      <div className="p-5 rounded-2xl bg-black/25 border border-white/08 space-y-4">
                        <div className="flex items-center justify-between pb-2 border-b border-white/06">
                          <span className="font-bold text-sm text-slate-200 flex items-center gap-2">
                            <Layers className="w-4 h-4 text-emerald-400" />
                            Black & White (B&W) Rates
                          </span>
                          <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">Standard</span>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-medium text-slate-400 mb-1.5">
                              Single-Sided Rate (₹ per page)
                            </label>
                            <div className="flex items-center rounded-xl bg-slate-950/80 border border-white/10 focus-within:border-emerald-500/60 focus-within:ring-2 focus-within:ring-emerald-500/20 transition-all overflow-hidden shadow-inner">
                              <span className="px-3.5 py-2.5 bg-white/05 text-emerald-400 font-mono font-bold text-sm border-r border-white/10 select-none">₹</span>
                              <input
                                type="number"
                                step="0.5"
                                min="0"
                                value={pricingConfig.bwSingle}
                                onChange={(e) => setPricingConfig({ ...pricingConfig, bwSingle: parseFloat(e.target.value) || 0 })}
                                className="w-full bg-transparent px-3.5 py-2 text-white font-mono text-sm font-bold outline-none placeholder:text-slate-600"
                                placeholder="0.0"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-slate-400 mb-1.5">
                              Double-Sided Rate (₹ per page / side)
                            </label>
                            <div className="flex items-center rounded-xl bg-slate-950/80 border border-white/10 focus-within:border-emerald-500/60 focus-within:ring-2 focus-within:ring-emerald-500/20 transition-all overflow-hidden shadow-inner">
                              <span className="px-3.5 py-2.5 bg-white/05 text-emerald-400 font-mono font-bold text-sm border-r border-white/10 select-none">₹</span>
                              <input
                                type="number"
                                step="0.5"
                                min="0"
                                value={pricingConfig.bwDouble}
                                onChange={(e) => setPricingConfig({ ...pricingConfig, bwDouble: parseFloat(e.target.value) || 0 })}
                                className="w-full bg-transparent px-3.5 py-2 text-white font-mono text-sm font-bold outline-none placeholder:text-slate-600"
                                placeholder="0.0"
                              />
                            </div>
                            <p className="text-[11px] text-slate-500 font-mono mt-1.5">
                              e.g. ₹{pricingConfig.bwDouble}/page = ₹{(pricingConfig.bwDouble * 2).toFixed(1)} per 2-sided physical sheet
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Color Rates */}
                      <div className={`p-5 rounded-2xl border space-y-4 transition-all ${pricingConfig.colorPrintEnabled ? 'bg-black/25 border-purple-500/25' : 'bg-black/15 border-white/05 opacity-60'
                        }`}>
                        <div className="flex items-center justify-between pb-2 border-b border-white/06">
                          <span className="font-bold text-sm text-slate-200 flex items-center gap-2">
                            <Palette className="w-4 h-4 text-purple-400" />
                            Color Print Rates
                          </span>
                          <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${pricingConfig.colorPrintEnabled ? 'text-purple-400 bg-purple-500/10 border-purple-500/20' : 'text-slate-500 bg-white/04 border-white/07'
                            }`}>
                            {pricingConfig.colorPrintEnabled ? 'Active' : 'Turned Off'}
                          </span>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-medium text-slate-400 mb-1.5">
                              Single-Sided Color Rate (₹ per page)
                            </label>
                            <div className={`flex items-center rounded-xl bg-slate-950/80 border transition-all overflow-hidden shadow-inner ${pricingConfig.colorPrintEnabled
                              ? 'border-white/10 focus-within:border-purple-500/60 focus-within:ring-2 focus-within:ring-purple-500/20'
                              : 'border-white/05 opacity-50 cursor-not-allowed'
                              }`}>
                              <span className="px-3.5 py-2.5 bg-white/05 text-purple-400 font-mono font-bold text-sm border-r border-white/10 select-none">₹</span>
                              <input
                                type="number"
                                step="0.5"
                                min="0"
                                disabled={!pricingConfig.colorPrintEnabled}
                                value={pricingConfig.colorSingle}
                                onChange={(e) => setPricingConfig({ ...pricingConfig, colorSingle: parseFloat(e.target.value) || 0 })}
                                className="w-full bg-transparent px-3.5 py-2 text-white font-mono text-sm font-bold outline-none placeholder:text-slate-600 disabled:cursor-not-allowed"
                                placeholder="0.0"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-slate-400 mb-1.5">
                              Double-Sided Color Rate (₹ per page / side)
                            </label>
                            <div className={`flex items-center rounded-xl bg-slate-950/80 border transition-all overflow-hidden shadow-inner ${pricingConfig.colorPrintEnabled
                              ? 'border-white/10 focus-within:border-purple-500/60 focus-within:ring-2 focus-within:ring-purple-500/20'
                              : 'border-white/05 opacity-50 cursor-not-allowed'
                              }`}>
                              <span className="px-3.5 py-2.5 bg-white/05 text-purple-400 font-mono font-bold text-sm border-r border-white/10 select-none">₹</span>
                              <input
                                type="number"
                                step="0.5"
                                min="0"
                                disabled={!pricingConfig.colorPrintEnabled}
                                value={pricingConfig.colorDouble}
                                onChange={(e) => setPricingConfig({ ...pricingConfig, colorDouble: parseFloat(e.target.value) || 0 })}
                                className="w-full bg-transparent px-3.5 py-2 text-white font-mono text-sm font-bold outline-none placeholder:text-slate-600 disabled:cursor-not-allowed"
                                placeholder="0.0"
                              />
                            </div>
                            <p className="text-[11px] text-slate-500 font-mono mt-1.5">
                              e.g. ₹{pricingConfig.colorDouble}/page = ₹{(pricingConfig.colorDouble * 2).toFixed(1)} per 2-sided physical sheet
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Example Calculation Card */}
                  <div className="p-4 rounded-xl bg-white/03 border border-white/07 text-xs space-y-2">
                    <div className="font-semibold text-slate-300 flex items-center gap-1.5">
                      <Receipt className="w-4 h-4 text-emerald-400" />
                      How Auto-Calculation Works on Orders:
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-slate-400 font-mono">
                      <div>• 10 pages B&W (Single-Sided): 10 × ₹{pricingConfig.bwSingle} = <strong className="text-emerald-400">₹{10 * pricingConfig.bwSingle}</strong></div>
                      <div>• 10 pages B&W (2-Sided): 10 × ₹{pricingConfig.bwDouble} = <strong className="text-emerald-400">₹{10 * pricingConfig.bwDouble}</strong></div>
                      <div>• 3 pages B&W (2-Sided): 2 back-to-back (₹{2 * pricingConfig.bwDouble}) + 1 single sheet side (₹{pricingConfig.bwSingle}) = <strong className="text-emerald-400">₹{(2 * pricingConfig.bwDouble) + pricingConfig.bwSingle}</strong></div>
                      <div>• 10 pages Color (2-Sided): 10 × ₹{pricingConfig.colorDouble} = <strong className="text-purple-400">₹{10 * pricingConfig.colorDouble}</strong></div>
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={handleSavePricing}
                      disabled={pricingSaving}
                      className="btn-primary w-full py-3 flex items-center justify-center gap-2 text-sm font-bold disabled:opacity-50"
                    >
                      {pricingSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Save Pricing & Color Settings
                    </button>
                  </div>
                </div>
              )}



              {/* ── BROWSER HARDWARE & DEVICE ACCESS ── */}
              {settingsSection === 'devices' && (
                <div className="glass-card rounded-2xl p-6 border border-white/07 space-y-6 animate-fade-in">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/07 pb-4">
                    <div>
                      <h2 className="text-base font-bold text-white flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-emerald-400" />
                        <span>Browser Hardware & Counter Device Access</span>
                      </h2>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Control which physical computers or browsers can log in to manage this shop counter. Approve new staff PCs or instantly revoke compromised devices.
                      </p>
                    </div>

                    <div className="flex items-center gap-2 self-start sm:self-auto">
                      <button
                        type="button"
                        onClick={loadShopDevices}
                        className="btn-secondary !py-1.5 !px-3 !text-xs flex items-center gap-1.5"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${loadingShopDevices ? 'animate-spin' : ''}`} />
                        <span>Refresh</span>
                      </button>
                    </div>
                  </div>

                  {/* Security Notice Card */}
                  <div className="p-4 rounded-xl bg-indigo-950/30 border border-indigo-500/20 flex items-start gap-3 text-xs text-indigo-200">
                    <ShieldCheck className="w-5 h-5 text-indigo-400 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <div className="font-bold text-white text-xs">
                        Zero-Trust Hardware Access Guarantee
                      </div>
                      <p className="text-[11px] text-indigo-200/80 leading-relaxed">
                        Even if someone obtains this shop's username and password on the same counter WiFi network, their browser is strictly locked out with <code>403 DEVICE_UNAPPROVED</code> until you click <strong>Approve Access</strong> below. When you revoke or delete a browser, active sessions are immediately terminated in real time.
                      </p>
                    </div>
                  </div>

                  {/* Devices List */}
                  {shopDevices.length === 0 ? (
                    <div className="p-8 rounded-2xl bg-black/20 border border-white/05 text-center space-y-2">
                      <div className="text-slate-400 text-xs font-semibold">
                        {loadingShopDevices ? 'Checking authorized devices...' : 'No browser hardware devices have connected to this shop yet.'}
                      </div>
                      <p className="text-[11px] text-slate-500 max-w-md mx-auto">
                        To connect a new counter computer: Have staff sign in on that PC at <code>/shop</code> with this shop's username & password. Their browser will appear here immediately as <strong>PENDING APPROVAL</strong> for 1-click access approval.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {shopDevices.map((dev) => {
                        const isCurrentBrowser = dev.id === localStorage.getItem('pb_browser_device_id');
                        const isApproved = Boolean(dev.approved);
                        const dateLabel = isApproved ? 'Accepted' : 'Requested';
                        const dateVal = dev.approved_at || dev.created_at;
                        const formattedDate = dateVal ? new Date(dateVal).toLocaleString() : 'N/A';

                        return (
                          <div
                            key={dev.id}
                            className={`p-4 rounded-2xl border transition-all ${!isApproved
                                ? 'bg-amber-950/20 border-amber-500/40 shadow-lg shadow-amber-500/5'
                                : isCurrentBrowser
                                  ? 'bg-emerald-950/20 border-emerald-500/30'
                                  : 'bg-white/5 border-white/10 hover:border-white/20'
                              }`}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                              <div className="flex items-start gap-3">
                                <div className={`p-2.5 rounded-xl border mt-0.5 ${!isApproved
                                    ? 'bg-amber-500/20 border-amber-500/30 text-amber-400'
                                    : isCurrentBrowser
                                      ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400'
                                      : 'bg-slate-800 border-white/10 text-slate-400'
                                  }`}>
                                  <Laptop className="w-5 h-5" />
                                </div>

                                <div className="space-y-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-bold text-white text-sm">
                                      {dev.label || 'Web Browser'}
                                    </span>
                                    {isCurrentBrowser && (
                                      <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-blue-500/20 text-blue-300 border border-blue-500/30">
                                        THIS PC (CURRENT BROWSER)
                                      </span>
                                    )}
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${isApproved
                                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                                        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30 animate-pulse'
                                      }`}>
                                      {isApproved ? '● APPROVED' : '⏳ PENDING APPROVAL'}
                                    </span>
                                  </div>

                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
                                    <span className="font-mono text-slate-300">
                                      IP: {dev.last_ip || 'Local Network'}
                                    </span>
                                    <span>•</span>
                                    <span className="text-slate-400">
                                      {dateLabel}: <strong className="text-slate-300">{formattedDate}</strong>
                                    </span>
                                    <span>•</span>
                                    <span className="text-slate-400">
                                      Last Seen: <span className="text-slate-300">{dev.last_seen_at ? new Date(dev.last_seen_at).toLocaleTimeString() : 'Never'}</span>
                                    </span>
                                    <span>•</span>
                                    <span className="font-mono text-slate-500 text-[11px]">
                                      ID: {dev.id.substring(0, 8)}...
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
                                {!isApproved ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => handleToggleShopDeviceApproval(dev.id, true)}
                                      className="px-3.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-md shadow-emerald-600/30 flex items-center gap-1.5 transition-all cursor-pointer"
                                    >
                                      <CheckCircle2 className="w-3.5 h-3.5" />
                                      <span>Approve Access</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteShopDevice(dev.id)}
                                      className="px-3 py-1.5 rounded-xl bg-rose-950/40 hover:bg-rose-900/60 text-rose-300 border border-rose-800/40 font-semibold text-xs transition-all cursor-pointer flex items-center gap-1.5"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                      <span>Deny</span>
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => handleToggleShopDeviceApproval(dev.id, false)}
                                      className="px-3 py-1.5 rounded-xl bg-rose-950/40 hover:bg-rose-900/60 text-rose-300 border border-rose-800/40 font-semibold text-xs transition-all cursor-pointer flex items-center gap-1.5"
                                    >
                                      <span>Revoke Access</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteShopDevice(dev.id)}
                                      className="px-2.5 py-1.5 rounded-xl bg-red-950/30 hover:bg-red-900/50 text-red-400 hover:text-red-200 border border-red-800/40 font-semibold text-xs transition-all cursor-pointer flex items-center gap-1"
                                      title="Delete device approval"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                      <span>Delete</span>
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ── CUSTOMER SUBSCRIPTION PASSES ── */}
              {settingsSection === 'subscribers' && (
                <div className="glass-card rounded-2xl p-6 border border-white/07 space-y-6 animate-fade-in">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/07 pb-4">
                    <div>
                      <h2 className="text-base font-bold text-white flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-amber-400" />
                        <span>Customer Subscriptions & Priority Passes</span>
                      </h2>
                      <p className="text-xs text-slate-400 mt-1">
                        Enrolled subscriber phone numbers automatically float to the top of your queue! Their order box features a distinctive golden Subscribed badge.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={loadCustomerSubs}
                      className="btn-secondary !py-1.5 !px-3 !text-xs flex items-center gap-1.5 self-start sm:self-auto"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${loadingSubs ? 'animate-spin' : ''}`} />
                      <span>Refresh</span>
                    </button>
                  </div>

                  {/* Form to Add / Enroll */}
                  <form onSubmit={handleAddSubscriber} className="p-4 rounded-xl bg-slate-900/60 border border-amber-500/30 space-y-3.5">
                    <div className="text-xs font-bold text-amber-300 uppercase tracking-wider flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" />
                      <span>Enroll New Priority Subscriber Number</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                      <div className="sm:col-span-5">
                        <label className="text-[11px] text-slate-400 font-medium block mb-1">Customer Phone Number</label>
                        <input
                          type="tel"
                          value={subForm.phone}
                          onChange={(e) => setSubForm({ ...subForm, phone: e.target.value })}
                          placeholder="+91 94285 53298"
                          className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white font-mono text-xs focus:border-amber-400 focus:outline-none"
                          required
                        />
                      </div>

                      <div className="sm:col-span-4">
                        <label className="text-[11px] text-slate-400 font-medium block mb-1">Client Name / Note (Optional)</label>
                        <input
                          type="text"
                          value={subForm.label}
                          onChange={(e) => setSubForm({ ...subForm, label: e.target.value })}
                          placeholder="e.g. Krish Patel (Semester Pass)"
                          className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white text-xs focus:border-amber-400 focus:outline-none"
                        />
                      </div>

                      <div className="sm:col-span-3">
                        <label className="text-[11px] text-slate-400 font-medium block mb-1">Pass Duration</label>
                        <select
                          value={subForm.days}
                          onChange={(e) => setSubForm({ ...subForm, days: Number(e.target.value) })}
                          className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white text-xs focus:border-amber-400 focus:outline-none font-medium"
                        >
                          <option value={7}>7 Days (1 Week)</option>
                          <option value={15}>15 Days</option>
                          <option value={30}>30 Days (1 Month)</option>
                          <option value={60}>60 Days (2 Months)</option>
                          <option value={90}>90 Days (3 Months)</option>
                          <option value={180}>180 Days (6 Months)</option>
                          <option value={365}>365 Days (1 Year)</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-1">
                      <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                        <span>Quick presets:</span>
                        {[7, 30, 90, 365].map((d) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => setSubForm({ ...subForm, days: d })}
                            className={`px-2 py-0.5 rounded text-[10px] font-mono transition-all ${subForm.days === d ? 'bg-amber-500 text-black font-bold' : 'bg-white/05 text-slate-300 hover:bg-white/10'}`}
                          >
                            {d}d
                          </button>
                        ))}
                      </div>

                      <button
                        type="submit"
                        disabled={subSaving}
                        className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold text-xs flex items-center gap-1.5 shadow-md shadow-amber-500/20 transition-all cursor-pointer disabled:opacity-50"
                      >
                        {subSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        <span>Save & Activate Pass</span>
                      </button>
                    </div>
                  </form>

                  {/* Subscribed Numbers List */}
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-bold text-white flex items-center gap-1.5">
                        <span>Active Priority Numbers ({customerSubs.length})</span>
                      </div>
                    </div>

                    {loadingSubs ? (
                      <div className="py-8 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                        <RefreshCw className="w-4 h-4 animate-spin text-amber-400" />
                        <span>Loading subscriptions...</span>
                      </div>
                    ) : customerSubs.length === 0 ? (
                      <div className="p-8 rounded-xl bg-black/20 border border-white/05 text-center space-y-2">
                        <Sparkles className="w-6 h-6 text-slate-500 mx-auto" />
                        <div className="text-xs text-slate-300 font-semibold">No Active Customer Subscriptions</div>
                        <div className="text-[11px] text-slate-500 max-w-sm mx-auto">
                          Add VIP customer numbers above. Whenever they WhatsApp documents to your shop, their orders will float to the top of your queue!
                        </div>
                      </div>
                    ) : (
                      <div className="divide-y divide-white/05 border border-white/07 rounded-xl overflow-hidden bg-black/20">
                        {customerSubs.map((sub: any) => {
                          const isExpired = !sub.isActive || new Date(sub.validUntil).getTime() <= Date.now();
                          return (
                            <div key={sub.id} className="p-3.5 flex flex-wrap items-center justify-between gap-3 hover:bg-white/[0.02] transition-colors">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isExpired ? 'bg-rose-500/10 text-rose-400' : 'bg-amber-500/15 text-amber-400'}`}>
                                  <Sparkles className="w-4 h-4" />
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-bold text-sm text-white font-mono">{sub.phone}</span>
                                    {sub.label && (
                                      <span className="text-xs px-2 py-0.5 rounded-md bg-white/05 text-slate-300 font-sans">
                                        {sub.label}
                                      </span>
                                    )}
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold font-mono ${isExpired ? 'bg-rose-500/20 text-rose-300' : 'bg-emerald-500/20 text-emerald-300'}`}>
                                      {isExpired ? '● EXPIRED' : `● ACTIVE (${sub.daysRemaining} days left)`}
                                    </span>
                                  </div>
                                  <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                                    Valid until: {new Date(sub.validUntil).toLocaleDateString()} · Added {new Date(sub.createdAt).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>

                              {/* Actions: Extend & Delete */}
                              <div className="flex items-center gap-2 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => handleExtendSubscriber(sub.id, 7)}
                                  className="px-2.5 py-1 rounded-lg bg-white/05 hover:bg-white/10 text-slate-300 hover:text-white text-xs font-semibold border border-white/10 transition-all cursor-pointer"
                                  title="Add 7 days to this pass"
                                >
                                  +7d
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleExtendSubscriber(sub.id, 30)}
                                  className="px-3 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-xs font-bold border border-amber-500/30 transition-all cursor-pointer flex items-center gap-1"
                                  title="Add 30 days (1 month) to this pass"
                                >
                                  <span>Extend +30d</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteSubscriber(sub.id, sub.phone)}
                                  className="p-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 text-xs transition-all cursor-pointer"
                                  title="Revoke priority subscription pass"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── SHOP AGENT (1-CLICK RUN & DOWNLOAD) ── */}
              {settingsSection === 'agent' && (
                <div className="glass-card rounded-2xl p-6 border border-white/07 space-y-6 animate-fade-in">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/07 pb-4">
                    <div>
                      <h2 className="text-base font-bold text-white flex items-center gap-2">
                        <Laptop className="w-5 h-5 text-emerald-400" />
                        Shop Print Agent (1-Click Run)
                      </h2>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Download and run the background print agent on this shop computer. Double-click to start anytime.
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${diagnostics?.agent?.online ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'}`} />
                      <span className={`text-xs font-bold ${diagnostics?.agent?.online ? 'text-emerald-400' : 'text-slate-500'}`}>
                        {diagnostics?.agent?.online ? 'Agent Connected' : 'Agent Offline'}
                      </span>
                    </div>
                  </div>

                  {/* Primary Download Card */}
                  <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-500/10 via-slate-900/90 to-brand-500/10 border border-emerald-500/30 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <div className="text-sm font-bold text-white flex items-center gap-2">
                          <Download className="w-4 h-4 text-emerald-400" />
                          Download Agent Package for this Shop
                        </div>
                        <p className="text-xs text-slate-400 mt-1 max-w-lg">
                          Pre-configured for this shop with your server address and credentials already embedded. Just extract and double-click <code>START_AGENT.bat</code>!
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={handleDownloadAgent}
                        disabled={downloadingAgent}
                        className="px-5 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-brand-600 hover:from-emerald-500 hover:to-brand-500 text-white font-bold text-xs shadow-lg shadow-emerald-600/30 flex items-center space-x-2 transition-all shrink-0 cursor-pointer text-center justify-center disabled:opacity-50"
                      >
                        {downloadingAgent ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        <span>{downloadingAgent ? 'Generating Package...' : 'Download Shop Agent (.zip)'}</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 text-[11px]">
                      <div className="p-3 rounded-xl bg-slate-950/60 border border-white/05 space-y-1">
                        <div className="font-bold text-emerald-400 flex items-center gap-1.5">
                          <Check className="w-3.5 h-3.5" /> 1. Double-Click to Start
                        </div>
                        <p className="text-slate-400">Double-click <code>START_AGENT.bat</code> to run the agent immediately.</p>
                      </div>
                      <div className="p-3 rounded-xl bg-slate-950/60 border border-white/05 space-y-1">
                        <div className="font-bold text-cyan-400 flex items-center gap-1.5">
                          <RefreshCw className="w-3.5 h-3.5" /> 2. When PC Restarts
                        </div>
                        <p className="text-slate-400">When you close your PC and turn it back on, just double-click <code>START_AGENT.bat</code> again.</p>
                      </div>
                      <div className="p-3 rounded-xl bg-slate-950/60 border border-white/05 space-y-1">
                        <div className="font-bold text-amber-400 flex items-center gap-1.5">
                          <FolderOpen className="w-3.5 h-3.5" /> 3. Direct Desktop Manual Prints
                        </div>
                        <p className="text-slate-400">Word, Excel, or images download <b>directly onto your Windows Desktop</b> screen!</p>
                      </div>
                    </div>
                  </div>

                  {/* 1-Click Autostart Option */}
                  <div className="p-4 rounded-xl bg-white/04 border border-white/07 space-y-2 text-xs">
                    <div className="font-bold text-white flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-brand-400" />
                      Want Auto-Start when PC Turns On? (Optional)
                    </div>
                    <p className="text-slate-400 text-[11px] leading-relaxed">
                      Inside the downloaded zip folder, there is also an <code>AUTOSTART_ON_BOOT.bat</code> file. Double-click it once, and Windows will automatically start the agent every morning whenever you turn on your PC!
                    </p>
                  </div>
                </div>
              )}

              {/* ── SYSTEM CONFIG ── */}
              {settingsSection === 'system' && (
                <div className="glass-card rounded-2xl p-6 border border-white/07 space-y-5 animate-fade-in">
                  <div className="border-b border-white/07 pb-4">
                    <h2 className="text-base font-bold text-white flex items-center gap-2">
                      <Monitor className="w-5 h-5 text-cyan-400" />
                      System Configuration
                    </h2>
                    <p className="text-[11px] text-slate-500 mt-1">23-hour expiration policy, manual print directory, and diagnostics.</p>
                  </div>

                  <div className="space-y-4 text-xs">
                    {/* Manual Dir */}
                    <div>
                      <label className="block text-slate-400 mb-1.5 font-semibold text-[11px] uppercase tracking-wider">Desktop Manual Print Directory</label>
                      <input type="text" readOnly value={adminConfig.manualDir} className="input-field font-mono !opacity-70 cursor-default" />
                      <p className="text-[11px] text-slate-500 mt-1.5">Images & presentations are copied here for manual alignment/scaling.</p>
                    </div>

                    {/* Expiration Safeguard */}
                    <div className="p-4 rounded-xl bg-amber-500/07 border border-amber-500/20 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-amber-300 flex items-center gap-2"><Clock className="w-4 h-4" />Order Expiration Safeguard</span>
                        <span className="text-amber-400 font-mono font-bold text-xs">AUTO PURGE</span>
                      </div>
                      <p className="text-[11px] text-amber-200/70 leading-relaxed">
                        Orders automatically expire and files are purged from disk according to the system expiration duration configured in Super Admin settings (e.g. 1 hour, 23 hours). This prevents disk bloat, protects customer privacy, and ensures optimal performance.
                      </p>
                    </div>

                    {/* Diagnostics */}
                    <div className="p-4 rounded-xl bg-white/04 border border-white/07 space-y-3">
                      <div className="font-bold text-white text-xs">System Diagnostics</div>
                      <div className="space-y-2 font-mono text-[11px]">
                        {[
                          { label: 'Print Agent', value: diagnostics?.agent?.online ? 'Online' : 'Offline', ok: diagnostics?.agent?.online },
                          { label: 'WhatsApp API', value: diagnostics?.whatsapp?.configured ? (diagnostics?.whatsapp?.isMetaApiReachable ? 'Connected' : 'Not Reachable') : 'Not Configured', ok: diagnostics?.whatsapp?.configured && diagnostics?.whatsapp?.isMetaApiReachable },
                          { label: 'Last Webhook', value: diagnostics?.webhook?.lastReceivedAt ? new Date(diagnostics.webhook.lastReceivedAt).toLocaleTimeString() : 'Awaiting', ok: !!diagnostics?.webhook?.lastReceivedAt },
                          { label: 'Shop Status', value: shop?.status || 'ACTIVE', ok: shop?.status !== 'EXPIRED' && shop?.status !== 'SUSPENDED' },
                        ].map(({ label, value, ok }) => (
                          <div key={label} className="flex items-center justify-between py-1.5 border-b border-white/05">
                            <span className="text-slate-500">{label}</span>
                            <span className={`font-semibold ${ok ? 'text-emerald-400' : 'text-rose-400'}`}>{value}</span>
                          </div>
                        ))}
                      </div>
                      <button onClick={loadDiagnostics} className="btn-secondary flex items-center gap-1.5 !text-xs">
                        <RefreshCw className="w-3 h-3" /> Refresh Diagnostics
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Toast />
        </div>
      );
    }

    return null;
  };
